import { useState, useEffect, useCallback, useMemo, useRef, FC } from 'react';
import { createPortal } from 'react-dom';
import {
  Tag,
  Plus,
  Sparkles,
  RefreshCw,
  Copy,
  Check,
  MousePointerClick,
  Send,
  Clock,
  Trash2,
  Ticket,
  AlertTriangle,
  CheckCircle2,
  Flame,
  ArrowLeft,
  Pause,
  Archive,
  RotateCcw,
  ArrowUpDown,
  History,
  CheckSquare,
  Eye,
  MoreVertical,
  Edit3,
  Bold,
  Italic,
  Strikethrough,
  Code,
  ChevronDown,
  ChevronRight,
  Settings as SettingsIcon,
  AlertCircle,
  X,
  Layers,
  Zap,
  Star,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../ui/Toast';
import { Button } from '../ui/Button';
import { Card } from '../ui/Card';
import { Badge } from '../ui/Badge';
import { Input } from '../ui/Input';
import { Drawer } from '../ui/Drawer';
import { ConfirmationModal } from '../ui/ConfirmationModal';
import { PageHeader } from '../widgets/PageHeader';
import { MetricWidget } from '../widgets/MetricWidget';
import { DataTable, Column } from '../widgets/DataTable';
import { WhatsAppBubblePreview } from '../widgets/WhatsAppBubblePreview';
import { LoadingSkeleton } from '../ui/LoadingSkeleton';
import {
  isMarketplaceConfigured,
  getAffiliateSettings,
} from '../../utils/affiliateSettings';
import {
  Offer,
  OfferCreatePayload,
  OfferMetricsSummary,
  OfferScrapeResult,
  fetchOffers,
  fetchOfferMetrics,
  createOffer,
  updateOffer,
  deleteOffer,
  scrapeOfferUrl,
  generateOfferCopies,
  bulkActionOffers,
} from '../../api/offers';
import { fetchGroups, WhatsAppGroup } from '../../api/groups';
import {
  enqueueOffer,
  DispatchQueue,
  fetchDispatchQueues,
  bulkAddQueue,
} from '../../api/queue';
import { CouponsView } from '../coupons/CouponsView';
import { ApplyCouponDrawer } from '../coupons/ApplyCouponDrawer';
import { applyCouponToOffer } from '../../api/coupons';
import { Tooltip } from '../ui/Tooltip';

const AVAILABLE_CATEGORIES = [
  'Tecnologia',
  'Casa e Cozinha',
  'Beleza e Cuidados',
  'Moda',
  'Gamer',
  'Bebê e Infantil',
  'Geral',
];

export const DEFAULT_PRODUCT_TEMPLATES = {
  urgency: `🚨 *PREÇO DESPENCOU! CORRE QUE VAI ACABAR!* 🚨

*{{nome_produto}}*

> De: ~{{preco_original}}~
> Por apenas: *{{preco}}* 🔥
{{bloco_cupom}}
⚡ _Menor preço registrado recentemente! Poucas unidades!_

🛒 *Compre aqui:* {{link_url}}`.trim(),

  look: `✨ *Gente, olha esse achadinho que encontrei pra vocês!* ✨

*{{nome_produto}}*

De: ~{{preco_original}}~
Por apenas: *{{preco}}* ✅
{{bloco_cupom}}
_Dica do grupo:_ Excelente custo-benefício, vale super a pena conferir! 💖

🛍️ *Acesse a oferta:* {{link_url}}`.trim(),

  minimalist: `📦 *{{nome_produto}}*

~{{preco_original}}~ ➔ *{{preco}}* 🔥
{{bloco_cupom}}
🛒 *Compre aqui:* {{link_url}}`.trim(),
};

export const DEFAULT_COUPON_TEMPLATES = {
  urgency: `🚨 *SAIU CUPOM EXCLUSIVO! CORRE PRA ATIVAR!* 🚨

*{{nome_produto}}*

> 🎟️ *Código do Cupom:* \`{{cupom}}\`
> 🔥 {{regras_cupom}}

🔗 *Ative aqui:* {{link_cupom}}
🛒 *Use no produto:* {{link_url}}`.trim(),

  look: `✨ *Olha esse cupom imperdível que consegui pra vocês!* ✨

*{{nome_produto}}*

🎟️ Cupom: *{{cupom}}*
_Válido no app enquanto durarem os resgates!_ 💖

👉 *Resgate o seu cupom:* {{link_cupom}}
🛍️ *Compre com desconto aqui:* {{link_url}}`.trim(),

  minimalist: `🎟️ *CUPOM DE DESCONTO*

*{{nome_produto}}*

Código: \`{{cupom}}\`
{{regras_cupom}}

🔗 {{link_cupom}}
🛒 {{link_url}}`.trim(),
};

export const DEFAULT_PRODUCT_TEMPLATE = DEFAULT_PRODUCT_TEMPLATES.urgency;
export const DEFAULT_COUPON_TEMPLATE = DEFAULT_COUPON_TEMPLATES.urgency;

export interface OffersViewProps {
  initialMode?: 'list' | 'create';
  initialOfferId?: string | null;
  initialAction?: 'view' | 'edit';
  initialTab?: 'offers' | 'cupons';
  initialOffers?: Offer[];
  initialQueueDrawerOpen?: boolean;
  initialDispatchQueues?: DispatchQueue[];
}

export const OffersView: FC<OffersViewProps> = ({
  initialMode = 'list',
  initialOfferId = null,
  initialAction = 'view',
  initialTab,
  initialOffers,
  initialQueueDrawerOpen,
  initialDispatchQueues,
}) => {
  const { token } = useAuth();
  const { success, error, info } = useToast();

  const [offersTab, setOffersTab] = useState<'offers' | 'cupons'>(() => {
    if (initialTab) return initialTab;
    if (
      typeof window !== 'undefined' &&
      window.location.pathname.includes('/cupons')
    ) {
      return 'cupons';
    }
    return 'offers';
  });
  const [couponTargetOffer, setCouponTargetOffer] = useState<Offer | null>(
    null
  );

  // Modo de exibição: listagem ou criação central dedicada
  const [viewMode, setViewMode] = useState<'list' | 'create'>(initialMode);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [selectedCategoryFilters, setSelectedCategoryFilters] = useState<
    string[]
  >([]);
  const [isDrawerPreviewExpanded, setIsDrawerPreviewExpanded] = useState(false);
  const [auditPage, setAuditPage] = useState(1);

  // Ordenação
  const [sortField, setSortField] = useState<
    | 'title'
    | 'promotional_price'
    | 'discount_percent'
    | 'clicks_count'
    | 'created_at'
    | 'quality_score'
  >('created_at');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  // Filtros Rápidos (Ticket 06: Nota 4.8+, Comissão > 10%, Envio Full)
  const [filterMinRating, setFilterMinRating] = useState<number | null>(null);
  const [filterMinCommission, setFilterMinCommission] = useState<number | null>(
    null
  );
  const [filterIsFull, setFilterIsFull] = useState(false);

  // Drawer Seletor de Fila (Ticket 06)
  const [isQueueSelectorOpen, setIsQueueSelectorOpen] = useState(
    initialQueueDrawerOpen ?? false
  );
  const [dispatchQueues, setDispatchQueues] = useState<DispatchQueue[]>(
    initialDispatchQueues ?? []
  );
  const [selectedQueueId, setSelectedQueueId] = useState<string>(() => {
    if (initialDispatchQueues && initialDispatchQueues.length > 0) {
      const def =
        initialDispatchQueues.find((q) => q.slug === 'principal') ||
        initialDispatchQueues[0];
      return def ? def.id : '';
    }
    return '';
  });
  const [isLoadingQueues, setIsLoadingQueues] = useState(false);

  // Listagem e Métricas
  const [offers, setOffers] = useState<Offer[]>(initialOffers ?? []);
  const [metrics, setMetrics] = useState<OfferMetricsSummary>({
    total_offers: 0,
    total_clicks: 0,
    queued_offers: 0,
    sent_offers: 0,
    pending_offers: 0,
    archived_offers: 0,
  });
  const [isLoading, setIsLoading] = useState(false);

  // Seleção Múltipla para Ações em Lote
  const [selectedOfferIds, setSelectedOfferIds] = useState<Set<string>>(
    new Set()
  );
  const [isBulkActing, setIsBulkActing] = useState(false);
  const [bulkDeleteModalOpen, setBulkDeleteModalOpen] = useState(false);

  // Estado do Formulário de Nova Oferta
  const [offerType, setOfferType] = useState<'product' | 'coupon'>('product');
  const [urlInput, setUrlInput] = useState('');
  const [isScraping, setIsScraping] = useState(false);

  // Campos do formulário
  const [title, setTitle] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [originalPrice, setOriginalPrice] = useState<string>('');
  const [promotionalPrice, setPromotionalPrice] = useState<string>('');
  const [hasProductCoupon, setHasProductCoupon] = useState(false);
  const [couponCode, setCouponCode] = useState('');
  const [couponLink, setCouponLink] = useState('');
  const [couponRules, setCouponRules] = useState('');
  const [affiliateUrl, setAffiliateUrl] = useState('');
  const [marketplace, setMarketplace] = useState('other');
  const [selectedCategories, setSelectedCategories] = useState<string[]>([
    'Geral',
  ]);
  const [extractionResult, setExtractionResult] = useState<{
    status: 'success' | 'warning' | 'error';
    title: string;
    message: string;
    marketplace: string;
    hasTitle: boolean;
    hasImage: boolean;
    hasPrice: boolean;
    hasAffiliation: boolean;
    priceValue?: string;
    actionRequired?:
      'configure_marketplace' | 'fill_price' | 'fill_image' | 'check_url';
  } | null>(null);
  const [isShortLink, setIsShortLink] = useState(false);
  const [showInShowcase, setShowInShowcase] = useState(true);
  const [sendToWhatsApp, setSendToWhatsApp] = useState(true);
  const [linkType, setLinkType] = useState<'api' | 'marketplace'>(() => {
    return getAffiliateSettings().defaultLinkType || 'api';
  });
  const [loadError, setLoadError] = useState<string | null>(null);

  // IA Copies
  // IA Copies (Inicializados com templates dinâmicos para cada abordagem)
  const [urgencyCopy, setUrgencyCopy] = useState(
    DEFAULT_PRODUCT_TEMPLATES.urgency
  );
  const [lookCopy, setLookCopy] = useState(DEFAULT_PRODUCT_TEMPLATES.look);
  const [minimalistCopy, setMinimalistCopy] = useState(
    DEFAULT_PRODUCT_TEMPLATES.minimalist
  );
  const [selectedVariation, setSelectedVariation] = useState<
    'urgency' | 'look' | 'minimalist'
  >('urgency');
  const [activeCopyText, setActiveCopyText] = useState(
    DEFAULT_PRODUCT_TEMPLATES.urgency
  );
  const [, setCopySource] = useState<'gemini' | 'local_fallback'>(
    'local_fallback'
  );
  const [isGeneratingCopy, setIsGeneratingCopy] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Barra de Variáveis Dinâmicas colapsável (padrão visível/aberta, colapsável pelo usuário)
  const [isVariablesExpanded, setIsVariablesExpanded] = useState(true);

  // Drawer de Detalhes
  const [selectedOfferForDrawer, setSelectedOfferForDrawer] =
    useState<Offer | null>(null);
  const [isUpdatingDrawer, setIsUpdatingDrawer] = useState(false);

  // Modal de Exclusão Individual
  const [deletingOffer, setDeletingOffer] = useState<Offer | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Edição de Rascunho / Oferta Existente
  const [editingOfferId, setEditingOfferId] = useState<string | null>(null);
  const [editingOfferStatus, setEditingOfferStatus] = useState<string | null>(
    null
  );
  const [isHotfixBannerDismissed, setIsHotfixBannerDismissed] = useState(false);
  const [editingOfferShortCode, setEditingOfferShortCode] = useState<
    string | null
  >(null);

  // Preview WhatsApp colapsável (padrão visível no desktop, colapsável)
  const [isPreviewExpanded, setIsPreviewExpanded] = useState(true);

  // Dropdown de ações na tabela (ID da oferta aberta e posição para portal flutuante)
  const [openDropdownId, setOpenDropdownId] = useState<string | null>(null);
  const [dropdownPosition, setDropdownPosition] = useState<{
    top: number;
    left: number;
    openUpwards: boolean;
  } | null>(null);

  // Feedback de Cópia
  const [copiedLink, setCopiedLink] = useState<string | null>(null);

  // Histórico de Ações colapsável no Drawer (padrão fechado para economizar espaço)
  const [isHistoryExpanded, setIsHistoryExpanded] = useState(false);

  // Ref do Textarea da Copy para inserção de formatação
  const copyTextareaRef = useRef<HTMLTextAreaElement>(null);

  // Notas expandidas no histórico do Drawer
  const [expandedNoteIndices, setExpandedNoteIndices] = useState<Set<number>>(
    new Set()
  );

  const [availableGroups, setAvailableGroups] = useState<WhatsAppGroup[]>([]);
  const [targetGroupIds, setTargetGroupIds] = useState<string[]>([]);
  const [sendToAllActive, setSendToAllActive] = useState(true);

  // Alterna formato do link e sincroniza imediatamente o texto da copy ativa
  const handleSelectLinkType = (newType: 'api' | 'marketplace') => {
    setLinkType(newType);
    const targetLink = affiliateUrl.trim() || urlInput.trim();
    const shortUrl = editingOfferShortCode
      ? `${typeof window !== 'undefined' ? window.location.origin : 'http://localhost:8000'}/r/${editingOfferShortCode}1`
      : `${typeof window !== 'undefined' ? window.location.origin : 'http://localhost:8000'}/r/:codigo1`;

    if (targetLink && shortUrl) {
      if (newType === 'marketplace' && activeCopyText.includes(shortUrl)) {
        setActiveCopyText((prev) => prev.split(shortUrl).join(targetLink));
      } else if (newType === 'api' && activeCopyText.includes(targetLink)) {
        setActiveCopyText((prev) => prev.split(targetLink).join(shortUrl));
      }
    }
  };

  // Ganchos Inteligentes Contextuais baseados na oferta (Desconto, Cupom, Categoria, Urgência)
  const recommendedHooks = useMemo(() => {
    const orig = originalPrice ? parseFloat(originalPrice) : null;
    const promo = promotionalPrice ? parseFloat(promotionalPrice) : null;
    const discountPct =
      orig && promo && orig > promo
        ? Math.round(((orig - promo) / orig) * 100)
        : 0;
    const isSuperCheap = promo !== null && promo < 30;
    const hasCoupon =
      offerType === 'coupon' ||
      (hasProductCoupon && Boolean(couponCode.trim()));

    const hooks: { label: string; text: string }[] = [];

    if (hasCoupon && offerType === 'product') {
      const storeName =
        marketplace !== 'other' ? marketplace.toUpperCase() : 'NESSE PREÇO';
      hooks.push({
        label: 'Exclusivo do Grupo',
        text: `${storeName} NESSE PREÇO É SÓ NO NOSSO GRUPINHO 😮‍💨`,
      });
      hooks.push({
        label: 'Combo Desconto + Cupom',
        text: '🔥 PROMOÇÃO + CUPOM LIBERADO! CORRE PRA ATIVAR! ⚡',
      });
    }

    if (discountPct >= 40 || isSuperCheap) {
      hooks.push({
        label: 'Quase de Graça',
        text: 'QUASE DE GRAÇA ESSA PERFEIÇÃO 🤏🏼',
      });
      hooks.push({
        label: 'Preço Despencou',
        text: '🚨 PREÇO DESPENCOU! CORRE QUE VAI ACABAR! 🚨',
      });
      hooks.push({
        label: 'Queda Brusca',
        text: '📉 QUEDA BRUSCA DE PREÇO! APROVEITEM! 🔥',
      });
    } else if (discountPct >= 20) {
      hooks.push({
        label: 'Baixou Demais',
        text: '🔥 BAIXOU DEMAIS! MENOR PREÇO HISTÓRICO! ⚡',
      });
      hooks.push({
        label: 'Achadinho',
        text: '✨ OLHA ESSE ACHADINHO QUE ENCONTREI PRA VOCÊS! ✨',
      });
    } else {
      hooks.push({
        label: 'Achadinho',
        text: '✨ OLHA ESSE ACHADINHO QUE ENCONTREI PRA VOCÊS! ✨',
      });
      hooks.push({
        label: 'Perfeição',
        text: 'QUASE DE GRAÇA ESSA PERFEIÇÃO 🤏🏼',
      });
      hooks.push({
        label: 'Urgência',
        text: '⚡ VAI ESGOTAR RÁPIDO! CORRE PRA GARANTIR! ⚡',
      });
    }

    if (offerType === 'coupon') {
      hooks.unshift({
        label: 'Cupom Urgente',
        text: `🚨 SAIU CUPOM ${marketplace !== 'other' ? marketplace.toUpperCase() : 'NOVO'} 🚨`,
      });
    }

    // Deduplica por texto e retorna até 4 opções
    const seen = new Set<string>();
    return hooks
      .filter((h) => {
        if (seen.has(h.text)) return false;
        seen.add(h.text);
        return true;
      })
      .slice(0, 4);
  }, [
    originalPrice,
    promotionalPrice,
    offerType,
    hasProductCoupon,
    couponCode,
    marketplace,
  ]);

  // Aplica o gancho selecionado substituindo a primeira linha da copy
  const handleApplyHook = (hookText: string) => {
    setActiveCopyText((prev) => {
      if (!prev.trim()) {
        return `${hookText}\n\n*${title || '{{nome_produto}}'}*\n\nDe: ~{{preco_original}}~\nPor apenas: *{{preco}}* 🔥\n{{bloco_cupom}}\n\n🛒 Compre aqui: {{link_url}}`;
      }
      const lines = prev.split('\n');
      lines[0] = hookText;
      return lines.join('\n');
    });
    info(
      `Gancho "${hookText.length > 25 ? hookText.substring(0, 25) + '...' : hookText}" aplicado à chamada da mensagem!`,
      'Gancho Aplicado'
    );
  };

  // Carrega dados da lista, métricas e grupos disponíveis
  const loadData = useCallback(async () => {
    if (!token) return;
    setIsLoading(true);
    setLoadError(null);
    try {
      const [offersData, metricsData, groupsData] = await Promise.all([
        fetchOffers(
          token,
          statusFilter !== 'all' ? { status: statusFilter } : undefined
        ),
        fetchOfferMetrics(token),
        fetchGroups(token).catch(() => [] as WhatsAppGroup[]),
      ]);
      setOffers(offersData);
      setMetrics(metricsData);
      setAvailableGroups(groupsData);
      setSelectedOfferIds(new Set());
      setLoadError(null);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      setLoadError(msg);
      error(msg, 'Erro ao carregar ofertas');
    } finally {
      setIsLoading(false);
    }
  }, [token, statusFilter, error]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (initialTab) {
      setOffersTab(initialTab);
    }
  }, [initialTab]);

  useEffect(() => {
    const handlePopState = () => {
      if (
        typeof window !== 'undefined' &&
        window.location.pathname.includes('/cupons')
      ) {
        setOffersTab('cupons');
      } else {
        setOffersTab('offers');
      }
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // Se veio initialOfferId, abre o Drawer de detalhes (view) ou edição (edit)
  useEffect(() => {
    if (initialOfferId && offers.length > 0) {
      const found = offers.find((o) => o.id === initialOfferId);
      if (found) {
        if (initialAction === 'edit') {
          handleEditOffer(found);
        } else {
          setViewMode('list');
          openDrawer(found);
        }
      }
    }
  }, [initialOfferId, initialAction, offers]);

  // Garante que grupos ativos sejam recarregados ao abrir criação de oferta
  useEffect(() => {
    if (viewMode === 'create' && token && availableGroups.length === 0) {
      fetchGroups(token)
        .then((groups) => {
          if (groups && groups.length > 0) {
            setAvailableGroups(groups);
          }
        })
        .catch(() => {});
    }
  }, [viewMode, token, availableGroups.length]);

  const [urlInputError, setUrlInputError] = useState<string | null>(null);

  // Alterna categorias no formulário
  const toggleCategory = (cat: string) => {
    if (selectedCategories.includes(cat)) {
      if (selectedCategories.length === 1) return;
      setSelectedCategories(selectedCategories.filter((c) => c !== cat));
    } else {
      setSelectedCategories([...selectedCategories, cat]);
    }
  };

  // Dispara scraping da URL
  const handleScrapeUrl = async () => {
    const trimmed = urlInput.trim();
    if (!token || !trimmed) {
      setUrlInputError('Insira um link ou texto de oferta válido.');
      error('Insira um link ou texto de oferta válido.', 'Falha no Scraping');
      return;
    }

    setUrlInputError(null);
    setExtractionResult(null);
    setIsScraping(true);
    try {
      const res: OfferScrapeResult = await scrapeOfferUrl(token, trimmed);
      setTitle(res.title);
      setImageUrl(res.image_url || '');
      setOriginalPrice(res.original_price ? String(res.original_price) : '');
      setPromotionalPrice(
        res.promotional_price ? String(res.promotional_price) : ''
      );
      setMarketplace(res.marketplace);
      setSelectedCategories(
        res.categories && res.categories.length > 0 ? res.categories : ['Geral']
      );

      const targetAffiliateUrl = res.affiliate_url || trimmed;
      if (res.affiliate_url) {
        setAffiliateUrl(res.affiliate_url);
      }
      if (res.engine_used) {
        setIsShortLink(res.engine_used === 'shopee_api');
      }

      // Valida se os dados essenciais foram identificados e afiliação configurada
      const isConfigured = isMarketplaceConfigured(res.marketplace);
      const hasTitle = Boolean(res.title && res.title.trim());
      const hasImage = Boolean(res.image_url && res.image_url.trim());
      const hasPrice = Boolean(
        (res.promotional_price && res.promotional_price > 0) ||
        (res.original_price && res.original_price > 0)
      );
      const isAffiliationReady = res.marketplace === 'other' || isConfigured;

      if (hasTitle && hasPrice && hasImage && isAffiliationReady) {
        setExtractionResult({
          status: 'success',
          title: 'Extração concluída',
          message: `Dados e afiliação (${res.marketplace.toUpperCase()}) extraídos com sucesso.`,
          marketplace: res.marketplace,
          hasTitle: true,
          hasImage: true,
          hasPrice: true,
          hasAffiliation: true,
          priceValue: res.promotional_price
            ? `R$ ${res.promotional_price.toFixed(2).replace('.', ',')}`
            : undefined,
        });
        success(
          'Metadados e afiliação extraídos com sucesso!',
          'Link Processado'
        );
      } else if (hasTitle) {
        let missingWarning = '';
        let action:
          | 'configure_marketplace'
          | 'fill_price'
          | 'fill_image'
          | 'check_url'
          | undefined;

        if (res.marketplace !== 'other' && !isConfigured) {
          missingWarning = `A loja ${res.marketplace.toUpperCase()} ainda não está configurada em Configurações.`;
          action = 'configure_marketplace';
        } else if (!hasPrice) {
          missingWarning =
            'Preço promocional não identificado no link. Preencha o valor manualmente abaixo.';
          action = 'fill_price';
        } else if (!hasImage) {
          missingWarning =
            'Imagem do produto não encontrada. Cole a URL da imagem manualmente abaixo.';
          action = 'fill_image';
        } else {
          missingWarning =
            'Algumas informações requerem sua conferência antes do envio.';
        }

        setExtractionResult({
          status: 'warning',
          title: 'Atenção aos dados',
          message: missingWarning,
          marketplace: res.marketplace,
          hasTitle,
          hasImage,
          hasPrice,
          hasAffiliation: isAffiliationReady,
          priceValue: res.promotional_price
            ? `R$ ${res.promotional_price.toFixed(2).replace('.', ',')}`
            : undefined,
          actionRequired: action,
        });
        info('Alguns dados requerem preenchimento manual.', 'Extração Parcial');
      } else {
        setExtractionResult({
          status: 'error',
          title: 'Falha na identificação',
          message:
            'Não foi possível extrair os dados deste link. Preencha os dados manualmente abaixo.',
          marketplace: res.marketplace,
          hasTitle: false,
          hasImage: false,
          hasPrice: false,
          hasAffiliation: false,
          actionRequired: 'check_url',
        });
        error('Não foi possível extrair metadados.', 'Falha na Extração');
      }

      setIsScraping(false);

      // Gera cópias com IA em segundo plano com seu próprio indicador de loading
      handleGenerateCopy({
        customTitle: res.title,
        customOrigPrice: res.original_price,
        customPromoPrice: res.promotional_price,
        customMarketplace: res.marketplace,
        customCats: res.categories,
        customAffiliateUrl: targetAffiliateUrl,
      }).catch((err) => {
        const msg = err instanceof Error ? err.message : String(err);
        error(msg, 'Erro ao gerar cópias com IA');
      });
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      setExtractionResult({
        status: 'error',
        title: 'Falha ao extrair dados do link',
        message: `Não foi possível carregar a página (${msg}). Verifique se o link está acessível no navegador ou preencha os dados manualmente abaixo.`,
        marketplace: 'other',
        hasTitle: false,
        hasImage: false,
        hasPrice: false,
        hasAffiliation: false,
        actionRequired: 'check_url',
      });
      error(msg, 'Falha no Scraping');
      setIsScraping(false);
    }
  };

  // Dispara geração de cópias com IA
  const handleGenerateCopy = async (overrides?: {
    customTitle?: string;
    customOrigPrice?: number | null;
    customPromoPrice?: number | null;
    customMarketplace?: string;
    customCats?: string[];
    customAffiliateUrl?: string;
  }) => {
    if (!token) return;

    const t = overrides?.customTitle || title;
    if (!t.trim()) {
      error('Preencha o título antes de gerar as cópias.', 'Título Necessário');
      return;
    }

    setIsGeneratingCopy(true);
    try {
      const orig =
        overrides?.customOrigPrice !== undefined
          ? overrides.customOrigPrice
          : originalPrice
            ? parseFloat(originalPrice)
            : null;
      const promo =
        overrides?.customPromoPrice !== undefined
          ? overrides.customPromoPrice
          : promotionalPrice
            ? parseFloat(promotionalPrice)
            : null;

      const finalLink =
        overrides?.customAffiliateUrl ||
        affiliateUrl.trim() ||
        urlInput.trim() ||
        null;

      const res = await generateOfferCopies(token, {
        title: t.trim(),
        original_price: orig,
        promotional_price: promo,
        coupon_code:
          offerType === 'coupon' || hasProductCoupon
            ? couponCode.trim() || null
            : null,
        is_coupon_only: offerType === 'coupon',
        coupon_rules: couponRules.trim() || null,
        affiliate_url: finalLink,
        marketplace: overrides?.customMarketplace || marketplace,
        categories: overrides?.customCats || selectedCategories,
      });

      // Se as cópias vieram do fallback local (offline / sem IA), garantimos que os templates
      // com variáveis dinâmicas permaneçam preservados em vez de hardcodar textos crus
      if (res.source === 'gemini') {
        setUrgencyCopy(res.urgency);
        setLookCopy(res.look);
        setMinimalistCopy(res.minimalist);
        setCopySource('gemini');

        // Aplica a variação ativa gerada pela IA
        if (selectedVariation === 'urgency') setActiveCopyText(res.urgency);
        else if (selectedVariation === 'look') setActiveCopyText(res.look);
        else if (selectedVariation === 'minimalist')
          setActiveCopyText(res.minimalist);

        success('3 opções de copy geradas com IA Gemini!', 'Cópias Geradas');
      } else {
        // Modo Local / Offline: Mantém os templates oficiais dinâmicos com variáveis
        const currentTemplates =
          offerType === 'product'
            ? DEFAULT_PRODUCT_TEMPLATES
            : DEFAULT_COUPON_TEMPLATES;
        setUrgencyCopy(currentTemplates.urgency);
        setLookCopy(currentTemplates.look);
        setMinimalistCopy(currentTemplates.minimalist);
        setCopySource('local_fallback');

        // Se o usuário estiver usando um template com variáveis, não sobrepõe com texto rígido
        if (
          !activeCopyText.trim() ||
          activeCopyText.includes('{{nome_produto}}') ||
          activeCopyText.includes('{{preco}}') ||
          activeCopyText.includes('{{link_url}}')
        ) {
          setActiveCopyText(currentTemplates[selectedVariation]);
        }

        info(
          'Templates dinâmicos estruturados com variáveis aplicados.',
          'Modelos com Variáveis'
        );
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      error(msg, 'Erro ao gerar cópias');
    } finally {
      setIsGeneratingCopy(false);
    }
  };

  // Salvar oferta no backend com validações e mensagens explícitas
  const handleSaveOffer = async (
    targetStatus: 'pending' | 'queued' = 'pending'
  ) => {
    if (!token) return;

    // 1. Validação do Título
    if (!title.trim()) {
      error('O título da oferta é obrigatório.', 'Título Necessário');
      return;
    }

    // 2. Validação da URL
    const destinationUrl = urlInput.trim() || affiliateUrl.trim();
    if (!destinationUrl) {
      error('O link original ou de afiliado é obrigatório.', 'Link Necessário');
      return;
    }

    // 3. Validações específicas para Enfileiramento Imediato ("Salvar na Fila")
    if (targetStatus === 'queued') {
      if (offerType === 'product') {
        const promo = promotionalPrice ? parseFloat(promotionalPrice) : null;
        if (promo === null || isNaN(promo) || promo <= 0) {
          error(
            'Informe um preço promocional válido (> 0) para enfileirar o produto.',
            'Preço Obrigatório'
          );
          return;
        }
      } else {
        if (!couponCode.trim()) {
          error(
            'Informe o código do cupom antes de enviar para a fila.',
            'Cupom Obrigatório'
          );
          return;
        }
      }

      // Se a copy estiver vazia, gera copy fallback inteligente para não travar o usuário
      let finalCopy =
        activeCopyText.trim() ||
        urgencyCopy.trim() ||
        lookCopy.trim() ||
        minimalistCopy.trim();

      if (!finalCopy) {
        const link = affiliateUrl.trim() || urlInput.trim();
        if (offerType === 'product') {
          const promo = promotionalPrice ? `R$ ${promotionalPrice}` : '';
          finalCopy = `🔥 *${title.trim()}*\n${promo ? `💥 Por apenas: ${promo}\n` : ''}🛒 Compre agora: ${link}`;
        } else {
          finalCopy = `🎟️ *Cupom Imperdível: ${title.trim()}*\n🏷️ Código: *${couponCode.trim()}*\n👉 Aproveite aqui: ${link}`;
        }
        setActiveCopyText(finalCopy);
        info(
          'Texto básico de divulgação gerado automaticamente.',
          'Copy Criada'
        );
      }
    }

    setIsSaving(true);
    try {
      const orig = originalPrice ? parseFloat(originalPrice) : null;
      const promo = promotionalPrice ? parseFloat(promotionalPrice) : null;

      const destLink = affiliateUrl.trim() || urlInput.trim();
      let finalCopy =
        activeCopyText.trim() ||
        urgencyCopy.trim() ||
        lookCopy.trim() ||
        minimalistCopy.trim();

      // Substitui placeholders residuais pelo link real
      if (destLink && finalCopy.includes('https://...')) {
        finalCopy = finalCopy.split('https://...').join(destLink);
      }

      // Se não houver copy gerada, monta copy estruturada completa
      if (!finalCopy) {
        if (offerType === 'product') {
          const promo = promotionalPrice ? `R$ ${promotionalPrice}` : '';
          finalCopy = `🔥 *${title.trim()}*\n${promo ? `💥 Por apenas: ${promo}\n` : ''}🛒 Compre agora: ${destLink}`;
        } else {
          finalCopy = `🎟️ *Cupom Imperdível: ${title.trim()}*\n🏷️ Código: *${couponCode.trim()}*\n👉 Aproveite aqui: ${destLink}`;
        }
      }

      // Garante que o link esteja presente no texto se informado (e não tiver sido usado como placeholder)
      const hasLinkPlaceholder =
        finalCopy.includes('{{link_url}}') ||
        finalCopy.includes('{{link}}') ||
        finalCopy.includes('{{url}}');
      if (destLink && !hasLinkPlaceholder && !finalCopy.includes(destLink)) {
        finalCopy = `${finalCopy}\n\n🛒 Compre aqui: ${destLink}`;
      }

      // Se for produto com preço promocional e a copy não tiver R$ nem placeholder de preço, injeta o preço
      const hasPricePlaceholder =
        finalCopy.includes('{{preco}}') ||
        finalCopy.includes('{{preco_promocional}}');
      if (
        offerType === 'product' &&
        promotionalPrice &&
        !hasPricePlaceholder &&
        !finalCopy.includes('R$') &&
        !finalCopy.includes(promotionalPrice)
      ) {
        finalCopy = `🔥 *${title.trim()}*\n💥 Por apenas: R$ ${promotionalPrice}\n\n${finalCopy}`;
      }

      if (!sendToWhatsApp && !showInShowcase) {
        error(
          'Selecione ao menos um canal de destino (Grupos de WhatsApp ou Vitrine Pública).',
          'Destino Obrigatório'
        );
        setIsSaving(false);
        return;
      }

      const effectiveStatus =
        editingOfferStatus === 'sent' && targetStatus !== 'queued'
          ? 'sent'
          : targetStatus;

      const payload: OfferCreatePayload = {
        title: title.trim(),
        original_url: urlInput.trim() || affiliateUrl.trim(),
        affiliate_url: affiliateUrl.trim() || null,
        image_url: imageUrl.trim() || null,
        original_price: orig,
        promotional_price: promo,
        coupon_code:
          offerType === 'coupon' || hasProductCoupon
            ? couponCode.trim() || null
            : null,
        coupon_link:
          offerType === 'coupon' || hasProductCoupon
            ? couponLink.trim() || null
            : null,
        is_coupon_only: offerType === 'coupon',
        categories: selectedCategories,
        marketplace,
        copy_urgency: urgencyCopy || null,
        copy_look: lookCopy || null,
        copy_minimalist: minimalistCopy || null,
        selected_copy: finalCopy || null,
        status: effectiveStatus,
        show_in_showcase: showInShowcase,
        send_to_whatsapp: sendToWhatsApp,
        link_type: linkType,
      };

      let savedOffer: Offer;
      if (editingOfferId) {
        savedOffer = await updateOffer(token, editingOfferId, payload);
        success(
          editingOfferStatus === 'sent'
            ? 'Oferta enviada atualizada! Cliques no link e vitrine refletem as novas informações.'
            : targetStatus === 'queued'
              ? 'Rascunho atualizado e enviado para a fila!'
              : 'Oferta atualizada com sucesso!',
          'Oferta Atualizada'
        );
      } else {
        savedOffer = await createOffer(token, payload);
        success(
          targetStatus === 'queued'
            ? 'Oferta adicionada à fila de envio com sucesso!'
            : 'Oferta salva em Aguardando.',
          'Oferta Registrada'
        );
      }

      // Se o objetivo é enviar para a fila, invoca a esteira oficial de disparos (/api/queue)
      if (targetStatus === 'queued') {
        const activeGroupIds = availableGroups
          .filter((g) => g.status === 'active')
          .map((g) => g.id);

        let finalGroupIds: string[] = [];
        if (sendToAllActive) {
          finalGroupIds = activeGroupIds;
        } else {
          finalGroupIds = targetGroupIds;
        }

        if (finalGroupIds.length > 0) {
          try {
            await enqueueOffer(token, {
              offer_id: savedOffer.id,
              group_ids: finalGroupIds,
            });
            success(
              `Agendamento confirmado para ${finalGroupIds.length} grupo(s).`,
              'Fila Atualizada'
            );
          } catch (queueErr: unknown) {
            const queueMsg =
              queueErr instanceof Error
                ? queueErr.message
                : 'Oferta salva, mas houve erro ao enfileirar para os grupos.';
            error(queueMsg, 'Aviso de Agendamento');
          }
        }
      }

      // Reseta e volta para listagem
      resetForm();
      setViewMode('list');
      await loadData();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      error(msg, 'Falha ao salvar');
    } finally {
      setIsSaving(false);
    }
  };

  // Carrega uma oferta existente para edição completa
  const handleEditOffer = (offer: Offer) => {
    setEditingOfferId(offer.id);
    setEditingOfferStatus(offer.status);
    setEditingOfferShortCode(offer.short_code || null);
    setOfferType(offer.is_coupon_only ? 'coupon' : 'product');
    setUrlInput(offer.original_url || '');
    setUrlInputError(null);
    setTitle(offer.title || '');
    setImageUrl(offer.image_url || '');
    setOriginalPrice(offer.original_price ? String(offer.original_price) : '');
    setPromotionalPrice(
      offer.promotional_price ? String(offer.promotional_price) : ''
    );
    setCouponCode(offer.coupon_code || '');
    setCouponLink(offer.coupon_link || '');
    setHasProductCoupon(
      !offer.is_coupon_only && Boolean(offer.coupon_code || offer.coupon_link)
    );
    setCouponRules('');
    setAffiliateUrl(offer.affiliate_url || '');
    setMarketplace(offer.marketplace || 'other');
    setSelectedCategories(
      offer.categories && offer.categories.length > 0
        ? offer.categories
        : ['Geral']
    );
    setExtractionResult(null);
    setUrgencyCopy(offer.copy_urgency || '');
    setLookCopy(offer.copy_look || '');
    setMinimalistCopy(offer.copy_minimalist || '');
    setActiveCopyText(offer.selected_copy || offer.copy_urgency || '');
    setSelectedVariation('urgency');
    setShowInShowcase(offer.show_in_showcase !== false);
    setLinkType(
      offer.link_type || getAffiliateSettings().defaultLinkType || 'api'
    );
    setSelectedOfferForDrawer(null);
    setViewMode('create');
  };

  // Insere formatação no texto da mensagem (WhatsApp Markdown)
  const handleInsertFormatting = (prefix: string, suffix: string = prefix) => {
    const textarea = copyTextareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const currentText = activeCopyText;
    const selected = currentText.substring(start, end);

    const replacement = `${prefix}${selected || 'texto'}${suffix}`;
    const newText =
      currentText.substring(0, start) +
      replacement +
      currentText.substring(end);

    setActiveCopyText(newText);

    setTimeout(() => {
      textarea.focus();
      if (selected) {
        textarea.setSelectionRange(start + prefix.length, end + prefix.length);
      } else {
        textarea.setSelectionRange(
          start + prefix.length,
          start + prefix.length + 5
        );
      }
    }, 10);
  };

  // Insere variável de template no texto da mensagem (ex: {{nome_produto}})
  const handleInsertTag = (tag: string) => {
    const textarea = copyTextareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const currentText = activeCopyText;

    const newText =
      currentText.substring(0, start) + tag + currentText.substring(end);

    setActiveCopyText(newText);

    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + tag.length, start + tag.length);
    }, 10);
  };

  const resetForm = () => {
    setEditingOfferId(null);
    setEditingOfferStatus(null);
    setIsHotfixBannerDismissed(false);
    setEditingOfferShortCode(null);
    setUrlInput('');
    setUrlInputError(null);
    setTitle('');
    setImageUrl('');
    setOriginalPrice('');
    setPromotionalPrice('');
    setCouponCode('');
    setCouponLink('');
    setHasProductCoupon(false);
    setCouponRules('');
    setAffiliateUrl('');
    setMarketplace('other');
    setSelectedCategories(['Geral']);
    setExtractionResult(null);
    setIsShortLink(false);
    setUrgencyCopy(DEFAULT_PRODUCT_TEMPLATES.urgency);
    setLookCopy(DEFAULT_PRODUCT_TEMPLATES.look);
    setMinimalistCopy(DEFAULT_PRODUCT_TEMPLATES.minimalist);
    setActiveCopyText(DEFAULT_PRODUCT_TEMPLATES.urgency);
    setOfferType('product');
    setShowInShowcase(true);
    setLinkType(getAffiliateSettings().defaultLinkType || 'api');
  };

  // Abre Drawer de Detalhamento
  const openDrawer = (offer: Offer) => {
    setSelectedOfferForDrawer(offer);
  };

  // Alterna visibilidade na Vitrine Pública via Drawer
  const handleToggleDrawerShowcase = async () => {
    if (!token || !selectedOfferForDrawer) return;

    const newVisibility = selectedOfferForDrawer.show_in_showcase === false;
    setIsUpdatingDrawer(true);
    try {
      const updated = await updateOffer(token, selectedOfferForDrawer.id, {
        show_in_showcase: newVisibility,
      });

      success(
        newVisibility
          ? 'Oferta marcada para exibição na Vitrine Pública!'
          : 'Oferta removida da Vitrine Pública.',
        'Visibilidade da Vitrine'
      );
      setSelectedOfferForDrawer(updated);
      await loadData();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      error(msg, 'Erro ao alterar visibilidade');
    } finally {
      setIsUpdatingDrawer(false);
    }
  };

  // Transição individual de status via Drawer ou Ação Rápida
  const handleTransitionStatus = async (
    offer: Offer,
    newStatus: 'pending' | 'queued' | 'archived',
    note?: string
  ) => {
    if (!token) return;

    // Se tentar enfileirar um rascunho incompleto
    if (newStatus === 'queued' && offer.is_draft) {
      error(
        'Esta oferta está com dados incompletos (Rascunho). Preencha preço, link e copy antes de enviar para a fila.',
        'Oferta Incompleta'
      );
      return;
    }

    try {
      const updated = await updateOffer(token, offer.id, {
        status: newStatus,
        status_note: note,
      });

      let msg = 'Status da oferta atualizado.';
      if (newStatus === 'queued') {
        const activeCount = availableGroups.filter(
          (g) => g.status === 'active'
        ).length;
        if (activeCount === 0) {
          info(
            'Oferta colocada na fila. Cadastre ao menos um Grupo Ativo para que a esteira inicie os envios automáticos.',
            'Aguardando Grupos Ativos'
          );
        } else {
          success(
            `Oferta enviada para a fila de envio (${activeCount} grupo(s) ativo(s))!`,
            'Fila Atualizada'
          );
        }
      } else if (newStatus === 'pending') {
        msg = 'Oferta pausada e devolvida para Aguardando.';
        success(msg, 'Status Alterado');
      } else if (newStatus === 'archived') {
        msg = 'Oferta arquivada com sucesso.';
        success(msg, 'Status Alterado');
      }

      if (selectedOfferForDrawer?.id === offer.id) {
        setSelectedOfferForDrawer(updated);
      }
      await loadData();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      error(msg, 'Falha na Transição');
    }
  };

  // Excluir oferta individual
  const handleDeleteOffer = async () => {
    if (!token || !deletingOffer) return;
    setIsDeleting(true);
    try {
      await deleteOffer(token, deletingOffer.id);
      success('Oferta removida com sucesso.', 'Oferta Excluída');
      if (selectedOfferForDrawer?.id === deletingOffer.id) {
        setSelectedOfferForDrawer(null);
      }
      setDeletingOffer(null);
      await loadData();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      error(msg, 'Erro ao excluir');
    } finally {
      setIsDeleting(false);
    }
  };

  // Copiar link rastreado
  const handleCopyTrackedUrl = (url: string, id: string) => {
    navigator.clipboard.writeText(url);
    setCopiedLink(id);
    success('Link copiado para a área de transferência!', 'Copiado');
    setTimeout(() => setCopiedLink(null), 2500);
  };

  // Seleção Múltipla
  const toggleSelectOffer = (id: string) => {
    const next = new Set(selectedOfferIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelectedOfferIds(next);
  };

  const toggleSelectAll = () => {
    if (selectedOfferIds.size === filteredAndSortedOffers.length) {
      setSelectedOfferIds(new Set());
    } else {
      setSelectedOfferIds(new Set(filteredAndSortedOffers.map((o) => o.id)));
    }
  };

  // Ações em Lote (Bulk Actions)
  const selectedOffersList = useMemo(() => {
    return offers.filter((o) => selectedOfferIds.has(o.id));
  }, [offers, selectedOfferIds]);

  const hasIncompleteDraftSelected = useMemo(() => {
    return selectedOffersList.some((o) => o.is_draft);
  }, [selectedOffersList]);

  const handleBulkAction = async (
    action: 'queue' | 'pause' | 'archive' | 'delete'
  ) => {
    if (!token || selectedOfferIds.size === 0) return;

    if (action === 'queue' && hasIncompleteDraftSelected) {
      error(
        'Existem rascunhos com dados incompletos selecionados. Complete-os antes de enviar para a fila.',
        'Ação Bloqueada'
      );
      return;
    }

    if (action === 'delete') {
      setBulkDeleteModalOpen(true);
      return;
    }

    setIsBulkActing(true);
    try {
      const res = await bulkActionOffers(token, {
        offer_ids: Array.from(selectedOfferIds),
        action,
      });

      if (res.errors.length > 0) {
        info(
          `${res.success_count} ofertas processadas com sucesso. ${res.failed_count} avisos.`,
          'Ação Parcial'
        );
      } else {
        success(
          `${res.success_count} ofertas atualizadas com sucesso!`,
          'Ação em Lote Concluída'
        );
      }
      setSelectedOfferIds(new Set());
      await loadData();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      error(msg, 'Erro na Ação em Lote');
    } finally {
      setIsBulkActing(false);
    }
  };

  const handleConfirmBulkDelete = async () => {
    if (!token || selectedOfferIds.size === 0) return;

    setIsBulkActing(true);
    try {
      const res = await bulkActionOffers(token, {
        offer_ids: Array.from(selectedOfferIds),
        action: 'delete',
      });

      success(
        `${res.success_count} ofertas excluídas definitivamente.`,
        'Exclusão em Lote'
      );
      setBulkDeleteModalOpen(false);
      setSelectedOfferIds(new Set());
      await loadData();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      error(msg, 'Erro ao excluir ofertas');
    } finally {
      setIsBulkActing(false);
    }
  };

  const openQueueSelector = useCallback(async () => {
    setIsQueueSelectorOpen(true);
    if (dispatchQueues.length === 0 && token) {
      setIsLoadingQueues(true);
      try {
        const queues = await fetchDispatchQueues(token);
        setDispatchQueues(queues);
        const def = queues.find((q) => q.slug === 'principal') || queues[0];
        if (def && !selectedQueueId) {
          setSelectedQueueId(def.id);
        }
      } catch (err: unknown) {
        const msg = err instanceof Error ? err.message : String(err);
        error(msg, 'Erro ao carregar filas');
      } finally {
        setIsLoadingQueues(false);
      }
    } else if (dispatchQueues.length > 0 && !selectedQueueId) {
      const def =
        dispatchQueues.find((q) => q.slug === 'principal') || dispatchQueues[0];
      if (def) setSelectedQueueId(def.id);
    }
  }, [token, dispatchQueues, selectedQueueId, error]);

  const handleConfirmBulkQueue = async () => {
    if (selectedOfferIds.size === 0) return;
    setIsBulkActing(true);
    try {
      const res = await bulkAddQueue(token || '', {
        offer_ids: Array.from(selectedOfferIds),
        queue_id: selectedQueueId || undefined,
      });

      if (res.success_count > 0) {
        const qName =
          dispatchQueues.find((q) => q.id === selectedQueueId)?.name ||
          'Fila Principal';
        success(
          `${res.success_count} ${
            res.success_count === 1
              ? 'oferta enfileirada'
              : 'ofertas enfileiradas'
          } na ${qName} com sucesso!`,
          'Enviado para a Fila'
        );
      }
      if (res.failed_count > 0) {
        error(
          `${res.failed_count} ofertas não puderam ser enfileiradas: ${res.errors.join(
            '; '
          )}`,
          'Aviso de Enfileiramento'
        );
      }
      setIsQueueSelectorOpen(false);
      setSelectedOfferIds(new Set());
      await loadData();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      error(msg, 'Erro ao enfileirar ofertas');
    } finally {
      setIsBulkActing(false);
    }
  };

  // Filtro Múltiplo de Categorias, Filtros Rápidos e Ordenação Interativa
  const filteredAndSortedOffers = useMemo(() => {
    let list = [...offers];

    if (selectedCategoryFilters.length > 0) {
      list = list.filter((o) =>
        selectedCategoryFilters.some((cat) => o.categories?.includes(cat))
      );
    }

    if (filterMinRating) {
      list = list.filter((o) => (o.rating ?? 0) >= 4.8);
    }

    if (filterMinCommission) {
      list = list.filter((o) => (o.commission ?? 0) > 10);
    }

    if (filterIsFull) {
      list = list.filter((o) => Boolean(o.is_full));
    }

    list.sort((a, b) => {
      let valA: string | number = 0;
      let valB: string | number = 0;

      if (sortField === 'title') {
        valA = (a.title || '').toLowerCase();
        valB = (b.title || '').toLowerCase();
      } else if (sortField === 'created_at') {
        valA = new Date(a.created_at).getTime();
        valB = new Date(b.created_at).getTime();
      } else if (sortField === 'quality_score') {
        valA = a.quality_score ?? 0;
        valB = b.quality_score ?? 0;
      } else {
        valA = (a[sortField] as number) || 0;
        valB = (b[sortField] as number) || 0;
      }

      if (valA < valB) return sortOrder === 'asc' ? -1 : 1;
      if (valA > valB) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });

    return list;
  }, [
    offers,
    selectedCategoryFilters,
    filterMinRating,
    filterMinCommission,
    filterIsFull,
    sortField,
    sortOrder,
  ]);

  const handleSortToggle = (
    field:
      | 'title'
      | 'promotional_price'
      | 'discount_percent'
      | 'clicks_count'
      | 'created_at'
      | 'quality_score'
  ) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('desc');
    }
  };

  // Colunas da DataTable
  const columns: Column<Offer>[] = [
    {
      key: 'select',
      header: (
        <div className="flex items-center justify-center">
          <input
            type="checkbox"
            checked={
              filteredAndSortedOffers.length > 0 &&
              selectedOfferIds.size === filteredAndSortedOffers.length
            }
            onChange={toggleSelectAll}
            aria-label="Selecionar todas as ofertas"
            className="h-4 w-4 cursor-pointer rounded border-surface-border text-brand-600 focus:ring-brand-500"
          />
        </div>
      ),
      className: 'w-10 text-center',
      render: (item) => (
        <div className="flex items-center justify-center">
          <input
            type="checkbox"
            checked={selectedOfferIds.has(item.id)}
            onChange={() => toggleSelectOffer(item.id)}
            aria-label={`Selecionar oferta ${item.title}`}
            className="h-4 w-4 cursor-pointer rounded border-surface-border text-brand-600 focus:ring-brand-500"
          />
        </div>
      ),
    },
    {
      key: 'title',
      header: (
        <button
          type="button"
          onClick={() => handleSortToggle('title')}
          className="flex items-center gap-1 font-semibold uppercase hover:text-content-main"
        >
          <span>Promoção / Oferta</span>
          <ArrowUpDown className="h-3 w-3 opacity-60" />
        </button>
      ),
      render: (item) => (
        <div className="flex items-center gap-2.5">
          {item.image_url ? (
            <img
              src={item.image_url}
              alt=""
              className="h-9 w-9 flex-shrink-0 rounded-lg border border-surface-border bg-surface-subtle object-cover"
            />
          ) : (
            <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg border border-surface-border bg-surface-subtle text-content-muted">
              {item.is_coupon_only ? (
                <Ticket className="h-4 w-4" />
              ) : (
                <Tag className="h-4 w-4" />
              )}
            </div>
          )}
          <div className="min-w-0 space-y-0.5">
            <span className="block max-w-[200px] truncate text-xs font-semibold text-content-main sm:max-w-xs md:max-w-sm">
              {item.title}
            </span>
            <div className="flex items-center gap-1.5 font-mono text-2xs text-content-muted">
              <span className="font-bold uppercase">{item.marketplace}</span>
              <span>•</span>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleCopyTrackedUrl(
                    item.group_tracked_url || item.tracked_url,
                    item.id
                  );
                }}
                className="flex cursor-pointer items-center gap-1 text-content-muted hover:text-content-main"
                title="Copiar link ultracurto para WhatsApp (/r/...1)"
              >
                <span>/r/{item.short_code}1</span>
                {copiedLink === item.id ? (
                  <Check className="h-3 w-3 text-brand-500" />
                ) : (
                  <Copy className="h-3 w-3" />
                )}
              </button>
            </div>
          </div>
        </div>
      ),
    },
    {
      key: 'price',
      header: (
        <button
          type="button"
          onClick={() => handleSortToggle('promotional_price')}
          className="flex items-center gap-1 font-semibold uppercase hover:text-content-main"
        >
          <span>Preço</span>
          <ArrowUpDown className="h-3 w-3 opacity-60" />
        </button>
      ),
      className: 'w-28',
      render: (item) => (
        <div className="space-y-0.5 text-xs">
          {item.promotional_price ? (
            <div className="flex items-center gap-1.5">
              <span className="font-medium text-content-main">
                {`R$ ${item.promotional_price.toFixed(2).replace('.', ',')}`}
              </span>
              {item.discount_percent && (
                <Badge variant="neutral" className="text-micro font-semibold">
                  {`-${item.discount_percent}%`}
                </Badge>
              )}
            </div>
          ) : item.is_coupon_only && item.coupon_code ? (
            <div className="flex items-center gap-1.5">
              <span className="font-mono text-xs font-medium text-content-main">
                {item.coupon_code}
              </span>
              <Badge variant="neutral" className="text-micro">
                Cupom
              </Badge>
            </div>
          ) : (
            <span className="text-content-muted">A definir</span>
          )}
        </div>
      ),
    },
    {
      key: 'clicks',
      header: (
        <button
          type="button"
          onClick={() => handleSortToggle('clicks_count')}
          className="flex items-center justify-center gap-1 font-semibold uppercase hover:text-content-main"
        >
          <span>Cliques</span>
          <ArrowUpDown className="h-3 w-3 opacity-60" />
        </button>
      ),
      className: 'text-center w-20',
      render: (item) => (
        <div className="flex items-center justify-center gap-1 font-mono text-xs font-medium text-content-muted">
          <MousePointerClick className="h-3.5 w-3.5" />
          <span className="font-semibold text-content-main">
            {item.clicks_count}
          </span>
        </div>
      ),
    },
    {
      key: 'quality_score',
      header: (
        <button
          type="button"
          onClick={() => handleSortToggle('quality_score')}
          className="flex items-center justify-center gap-1 font-semibold uppercase hover:text-content-main"
        >
          <span>Score</span>
          <ArrowUpDown className="h-3 w-3 opacity-60" />
        </button>
      ),
      className: 'text-center w-28',
      render: (item) => {
        const score =
          typeof item.quality_score === 'number'
            ? Math.round(item.quality_score * 100)
            : null;
        return (
          <div className="flex items-center justify-center gap-1">
            {score !== null ? (
              <Badge
                variant={
                  score >= 70 ? 'online' : score >= 50 ? 'warning' : 'neutral'
                }
                className="font-mono text-micro font-semibold"
              >
                {`⭐ ${item.rating ? item.rating.toFixed(1) : '-'} | ${score}%`}
              </Badge>
            ) : (
              <span className="text-micro text-content-muted">-</span>
            )}
          </div>
        );
      },
    },
    {
      key: 'status',
      header: 'Status',
      render: (item) => (
        <div className="flex items-center gap-1.5">
          <Badge
            variant={
              item.status === 'sent'
                ? 'online'
                : item.status === 'queued'
                  ? 'warning'
                  : item.status === 'error'
                    ? 'danger'
                    : item.status === 'archived'
                      ? 'offline'
                      : 'neutral'
            }
            withDot
          >
            {item.status === 'sent'
              ? 'Enviada'
              : item.status === 'queued'
                ? 'Na Fila'
                : item.status === 'error'
                  ? 'Falha / Quarentena'
                  : item.status === 'archived'
                    ? 'Arquivada'
                    : 'Aguardando'}
          </Badge>
          {item.status === 'pending' && item.is_draft && (
            <Badge variant="warning" className="text-micro font-bold">
              Rascunho
            </Badge>
          )}
        </div>
      ),
    },
    {
      key: 'actions',
      header: 'Ações',
      className: 'text-right w-24',
      render: (item) => {
        const isMenuOpen = openDropdownId === item.id;

        return (
          <div className="relative flex items-center justify-end gap-1">
            {/* Botão rápido para ver detalhes */}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => openDrawer(item)}
              title="Ver Detalhes e Telemetria"
              aria-label={`Ver Detalhes da oferta ${item.title}`}
            >
              <Eye className="h-3.5 w-3.5 text-content-muted hover:text-content-main" />
            </Button>

            {/* Ação Rápida: Aplicar Cupom */}
            <Tooltip content="Aplicar Cupom com 1 Clique">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setCouponTargetOffer(item)}
                title="Aplicar Cupom"
                aria-label={`Aplicar Cupom na oferta ${item.title}`}
              >
                <Ticket className="h-3.5 w-3.5 text-brand-500" />
              </Button>
            </Tooltip>

            {/* Menu de Ações Secundárias */}
            <div className="relative inline-block text-left">
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  if (isMenuOpen) {
                    setOpenDropdownId(null);
                    setDropdownPosition(null);
                  } else {
                    const rect = e.currentTarget.getBoundingClientRect();
                    const spaceBelow = window.innerHeight - rect.bottom;
                    const openUp = spaceBelow < 220;
                    setDropdownPosition({
                      top: openUp ? rect.top - 8 : rect.bottom + 4,
                      left: Math.max(16, rect.right - 192),
                      openUpwards: openUp,
                    });
                    setOpenDropdownId(item.id);
                  }
                }}
                title="Mais Ações"
                aria-label={`Mais Ações da oferta ${item.title}`}
                className={
                  isMenuOpen
                    ? 'bg-surface-subtle text-content-main'
                    : 'text-content-muted'
                }
              >
                <MoreVertical className="h-3.5 w-3.5" />
              </Button>

              {isMenuOpen &&
                dropdownPosition &&
                typeof document !== 'undefined' &&
                createPortal(
                  <>
                    {/* Backdrop invisível para fechar ao clicar fora */}
                    <div
                      className="fixed inset-0 z-50 bg-transparent"
                      onClick={(e) => {
                        e.stopPropagation();
                        setOpenDropdownId(null);
                        setDropdownPosition(null);
                      }}
                    />

                    {/* Dropdown Popover Flutuante via Portal fora da tabela */}
                    <div
                      style={{
                        position: 'fixed',
                        zIndex: 9999,
                        top: dropdownPosition.openUpwards
                          ? undefined
                          : `${dropdownPosition.top}px`,
                        bottom: dropdownPosition.openUpwards
                          ? `${window.innerHeight - dropdownPosition.top}px`
                          : undefined,
                        left: `${dropdownPosition.left}px`,
                      }}
                      className="animate-in fade-in zoom-in-95 w-48 rounded-lg border border-surface-border bg-surface-panel p-1 shadow-xl duration-100"
                    >
                      {/* Aplicar Cupom */}
                      <button
                        type="button"
                        onClick={() => {
                          setOpenDropdownId(null);
                          setDropdownPosition(null);
                          setCouponTargetOffer(item);
                        }}
                        className="flex w-full items-center gap-2 rounded-md px-2.5 py-1.5 text-left text-xs font-medium text-brand-600 hover:bg-brand-50 dark:text-brand-400 dark:hover:bg-brand-950/30"
                      >
                        <Ticket className="h-3.5 w-3.5" />
                        <span>Aplicar Cupom</span>
                      </button>

                      {/* Editar */}
                      <button
                        type="button"
                        onClick={() => {
                          setOpenDropdownId(null);
                          setDropdownPosition(null);
                          handleEditOffer(item);
                        }}
                        className="flex w-full items-center gap-2 rounded-md px-2.5 py-1.5 text-left text-xs font-medium text-content-main hover:bg-surface-subtle"
                      >
                        <Edit3 className="h-3.5 w-3.5 text-content-muted" />
                        <span>Editar Oferta</span>
                      </button>

                      {/* Transição: Reenviar para Fila (Sent) */}
                      {item.status === 'sent' && (
                        <button
                          type="button"
                          onClick={() => {
                            setOpenDropdownId(null);
                            setDropdownPosition(null);
                            handleTransitionStatus(item, 'queued');
                          }}
                          className="flex w-full items-center gap-2 rounded-md px-2.5 py-1.5 text-left text-xs font-medium text-brand-600 hover:bg-brand-50 dark:text-brand-400 dark:hover:bg-brand-950/30"
                        >
                          <Send className="h-3.5 w-3.5" />
                          <span>Reenviar para a Fila</span>
                        </button>
                      )}

                      {/* Transição: Fila (Pending ou Error) */}
                      {(item.status === 'pending' ||
                        item.status === 'error') && (
                        <button
                          type="button"
                          disabled={item.is_draft}
                          onClick={() => {
                            setOpenDropdownId(null);
                            setDropdownPosition(null);
                            handleTransitionStatus(item, 'queued');
                          }}
                          title={
                            item.is_draft
                              ? 'Complete preço, link e copy para enviar para a fila'
                              : item.status === 'error'
                                ? 'Reenviar oferta da quarentena para a fila de disparo'
                                : 'Enviar oferta para a fila de disparo'
                          }
                          className={`flex w-full items-center gap-2 rounded-md px-2.5 py-1.5 text-left text-xs font-medium ${
                            item.is_draft
                              ? 'cursor-not-allowed text-content-muted opacity-50'
                              : 'text-brand-600 hover:bg-brand-50 dark:text-brand-400 dark:hover:bg-brand-950/30'
                          }`}
                        >
                          <Send className="h-3.5 w-3.5" />
                          <span>
                            {item.status === 'error'
                              ? 'Reenviar para a Fila'
                              : 'Enviar para a Fila'}
                          </span>
                        </button>
                      )}

                      {/* Transição: Pausar */}
                      {(item.status === 'queued' ||
                        item.status === 'error') && (
                        <button
                          type="button"
                          onClick={() => {
                            setOpenDropdownId(null);
                            setDropdownPosition(null);
                            handleTransitionStatus(item, 'pending');
                          }}
                          className="flex w-full items-center gap-2 rounded-md px-2.5 py-1.5 text-left text-xs font-medium text-amber-600 hover:bg-amber-50 dark:text-amber-400 dark:hover:bg-amber-950/30"
                        >
                          <Pause className="h-3.5 w-3.5" />
                          <span>Devolver para Aguardando</span>
                        </button>
                      )}

                      {/* Transição: Restaurar */}
                      {item.status === 'archived' && (
                        <button
                          type="button"
                          onClick={() => {
                            setOpenDropdownId(null);
                            setDropdownPosition(null);
                            handleTransitionStatus(item, 'pending');
                          }}
                          className="flex w-full items-center gap-2 rounded-md px-2.5 py-1.5 text-left text-xs font-medium text-brand-600 hover:bg-brand-50 dark:text-brand-400 dark:hover:bg-brand-950/30"
                        >
                          <RotateCcw className="h-3.5 w-3.5" />
                          <span>Restaurar Oferta</span>
                        </button>
                      )}

                      {/* Transição: Arquivar */}
                      {item.status !== 'archived' && (
                        <button
                          type="button"
                          onClick={() => {
                            setOpenDropdownId(null);
                            setDropdownPosition(null);
                            handleTransitionStatus(item, 'archived');
                          }}
                          className="flex w-full items-center gap-2 rounded-md px-2.5 py-1.5 text-left text-xs font-medium text-content-muted hover:bg-surface-subtle hover:text-content-main"
                        >
                          <Archive className="h-3.5 w-3.5" />
                          <span>Arquivar</span>
                        </button>
                      )}

                      <div className="my-1 border-t border-surface-border" />

                      {/* Excluir */}
                      <button
                        type="button"
                        onClick={() => {
                          setOpenDropdownId(null);
                          setDropdownPosition(null);
                          setDeletingOffer(item);
                        }}
                        className="flex w-full items-center gap-2 rounded-md px-2.5 py-1.5 text-left text-xs font-medium text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950/30"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                        <span>Excluir</span>
                      </button>
                    </div>
                  </>,
                  document.body
                )}
            </div>
          </div>
        );
      },
    },
  ];

  return (
    <div className="space-y-6">
      {viewMode === 'list' && offersTab === 'cupons' ? (
        <CouponsView />
      ) : (
        <>
          {/* 1. PageHeader Oficial */}
          <PageHeader
            title={
              viewMode === 'list'
                ? 'Módulo de Ofertas'
                : editingOfferId
                  ? 'Editar Oferta'
                  : 'Nova Oferta'
            }
            subtitle={
              viewMode === 'list'
                ? 'Ingestão automatizada, geração persuasiva com IA e telemetria de cliques em tempo real'
                : editingOfferId
                  ? 'Atualize os detalhes da promoção ou substitua o link e a copy'
                  : 'Cadastre uma promoção, extraia metadados e gere copies personalizadas'
            }
            badge={
              <Badge variant="online" withDot>
                Motor Ativo
              </Badge>
            }
            actions={
              viewMode === 'create' ? (
                <Button
                  variant="secondary"
                  size="sm"
                  leftIcon={<ArrowLeft className="h-3.5 w-3.5" />}
                  onClick={() => {
                    resetForm();
                    setViewMode('list');
                  }}
                >
                  Voltar para Lista
                </Button>
              ) : (
                <Button
                  variant="primary"
                  size="sm"
                  leftIcon={<Plus className="h-3.5 w-3.5" />}
                  onClick={() => {
                    resetForm();
                    setViewMode('create');
                  }}
                >
                  Nova Oferta
                </Button>
              )
            }
          />

          {/* 2. Grid de Telemetria Operacional (Visível na listagem de ofertas) */}
          {viewMode === 'list' && (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4 lg:grid-cols-4">
              <MetricWidget
                label="Total de Promoções"
                value={String(metrics.total_offers)}
                description="Ofertas registradas no catálogo"
                icon={Tag}
              />
              <MetricWidget
                label="Cliques nos Links"
                value={String(metrics.total_clicks)}
                description="Acessos via rota /r/:code"
                icon={MousePointerClick}
                trend={
                  metrics.total_clicks > 0
                    ? {
                        value: `${metrics.total_clicks} cliques`,
                        positive: true,
                      }
                    : undefined
                }
              />
              <MetricWidget
                label="Na Fila de Envio"
                value={String(metrics.queued_offers)}
                description="Prontas para disparo agendado"
                icon={Clock}
              />
              <MetricWidget
                label="Enviadas nos Grupos"
                value={String(metrics.sent_offers)}
                description="Disparadas aos membros dos grupos"
                icon={Send}
              />
            </div>
          )}

          {/* 3. Modo de Criação Dedicada (Página Completa) */}
          {viewMode === 'create' ? (
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
              {/* Coluna Esquerda: Preview do Balão WhatsApp (Opcional e Colapsável, agora alinhado ao botão da barra inferior) */}
              {isPreviewExpanded && (
                <div className="space-y-4 lg:col-span-5">
                  <div className="sticky top-6 space-y-3">
                    <div className="flex items-center justify-between">
                      <h3 className="text-xs font-bold uppercase tracking-wider text-content-muted">
                        Preview no WhatsApp
                      </h3>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setIsPreviewExpanded(false)}
                        className="text-2xs text-content-muted hover:text-content-main"
                      >
                        Ocultar Preview
                      </Button>
                    </div>
                    <WhatsAppBubblePreview
                      imageUrl={imageUrl}
                      title={title}
                      text={(() => {
                        let rendered =
                          activeCopyText ||
                          'Cole uma URL ou digite sua copy...';
                        const targetLink =
                          affiliateUrl.trim() || urlInput.trim();
                        const shortUrl = editingOfferShortCode
                          ? `${typeof window !== 'undefined' ? window.location.origin : 'http://localhost:8000'}/r/${editingOfferShortCode}1`
                          : `${typeof window !== 'undefined' ? window.location.origin : 'http://localhost:8000'}/r/:codigo1`;

                        const finalLink =
                          linkType === 'marketplace'
                            ? targetLink || shortUrl
                            : shortUrl || targetLink;

                        // Formatações de preço para o preview
                        const origNum = originalPrice
                          ? parseFloat(originalPrice)
                          : null;
                        const promoNum = promotionalPrice
                          ? parseFloat(promotionalPrice)
                          : null;
                        const fmtPrice = (val: number | null) =>
                          val !== null && !isNaN(val)
                            ? `R$ ${val.toFixed(2).replace('.', ',')}`
                            : '';
                        const discountPct =
                          origNum && promoNum && origNum > promoNum
                            ? `${Math.round(((origNum - promoNum) / origNum) * 100)}% OFF`
                            : '';

                        const couponCodeVal = couponCode.trim();
                        const couponLinkVal = couponLink.trim();
                        let blocoCupom = '';
                        if (couponCodeVal) {
                          if (couponLinkVal) {
                            blocoCupom = `> use o cupom *${couponCodeVal}* resgate aqui: ${couponLinkVal}`;
                          } else {
                            blocoCupom = `> use o cupom: *${couponCodeVal}*`;
                          }
                        }

                        // Resolução de variáveis de template na visualização
                        const templateMap: Record<string, string> = {
                          '{{nome_produto}}': title.trim() || 'Nome do Produto',
                          '{{titulo}}': title.trim() || 'Nome do Produto',
                          '{{produto}}': title.trim() || 'Nome do Produto',
                          '{{link_url}}': finalLink,
                          '{{link}}': finalLink,
                          '{{url}}': finalLink,
                          '{{preco}}': fmtPrice(promoNum) || 'R$ 0,00',
                          '{{preco_promocional}}':
                            fmtPrice(promoNum) || 'R$ 0,00',
                          '{{preco_original}}': fmtPrice(origNum) || '',
                          '{{preco_de}}': fmtPrice(origNum) || '',
                          '{{preco_de_riscado}}':
                            origNum !== null ? `~${fmtPrice(origNum)}~` : '',
                          '{{preco_por_negrito}}':
                            promoNum !== null
                              ? `*${fmtPrice(promoNum)}*`
                              : fmtPrice(promoNum) || 'R$ 0,00',
                          '{{bloco_cupom}}': blocoCupom,
                          '{{desconto}}': discountPct,
                          '{{cupom}}': couponCodeVal,
                          '{{codigo_cupom}}': couponCodeVal,
                          '{{link_cupom}}': couponLinkVal || finalLink,
                          '{{cupom_link}}': couponLinkVal || finalLink,
                          '{{regras_cupom}}':
                            couponRules.trim() ||
                            (couponCodeVal
                              ? `Desconto especial com o cupom ${couponCodeVal}`
                              : ''),
                        };

                        for (const [tag, val] of Object.entries(templateMap)) {
                          if (rendered.includes(tag)) {
                            rendered = rendered.split(tag).join(val);
                          }
                        }

                        if (!blocoCupom) {
                          rendered = rendered
                            .split('\n{{bloco_cupom}}')
                            .join('')
                            .split('{{bloco_cupom}}')
                            .join('');
                        }

                        if (!origNum) {
                          rendered = rendered
                            .split('> De: ~~\n')
                            .join('')
                            .split('De: ~~\n')
                            .join('')
                            .split('~~ ➔ ')
                            .join('');
                        }

                        if (linkType === 'marketplace') {
                          if (
                            shortUrl &&
                            targetLink &&
                            rendered.includes(shortUrl)
                          ) {
                            rendered = rendered
                              .split(shortUrl)
                              .join(targetLink);
                          }
                        } else {
                          if (
                            targetLink &&
                            shortUrl &&
                            rendered.includes(targetLink)
                          ) {
                            rendered = rendered
                              .split(targetLink)
                              .join(shortUrl);
                          }
                        }

                        return rendered;
                      })()}
                      originalPrice={
                        originalPrice ? parseFloat(originalPrice) : null
                      }
                      promotionalPrice={
                        promotionalPrice ? parseFloat(promotionalPrice) : null
                      }
                      couponCode={
                        offerType === 'coupon' || hasProductCoupon
                          ? couponCode
                          : undefined
                      }
                      isCouponOnly={offerType === 'coupon'}
                      marketplace={marketplace}
                    />
                  </div>
                </div>
              )}

              {/* Coluna Direita: Formulário de Configuração */}
              <div
                className={`space-y-5 transition-all duration-200 ${
                  isPreviewExpanded ? 'lg:col-span-7' : 'lg:col-span-12'
                }`}
              >
                {/* Aviso Sucinto para Edição de Oferta Já Enviada (Alerta Descartável) */}
                {editingOfferStatus === 'sent' && !isHotfixBannerDismissed && (
                  <div className="relative flex items-start gap-2.5 rounded-xl border border-amber-500/30 bg-amber-500/10 p-3.5 text-xs dark:border-amber-500/20 dark:bg-amber-500/5">
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" />
                    <div className="min-w-0 flex-1 space-y-1 pr-6">
                      <p className="font-semibold text-amber-900 dark:text-amber-300">
                        Editando oferta já enviada
                      </p>
                      <p className="text-2xs leading-relaxed text-content-muted">
                        A mensagem já entregue no WhatsApp{' '}
                        <strong className="text-content-main">
                          não será alterada
                        </strong>
                        . Novos cliques no link da conversa serão direcionados à{' '}
                        <strong className="text-content-main">nova URL</strong>{' '}
                        e os dados atualizados refletirão na vitrine.
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setIsHotfixBannerDismissed(true)}
                      title="Dispensar aviso"
                      aria-label="Dispensar aviso"
                      className="absolute right-2.5 top-2.5 cursor-pointer rounded-lg p-1 text-content-muted hover:bg-surface-subtle hover:text-content-main"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                )}

                {/* Seletor de Tipo: Produto vs Apenas Cupom */}
                <Card>
                  <Card.Body className="space-y-4">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-semibold text-content-main">
                        Tipo de Oferta
                      </span>
                      <div className="flex items-center gap-1 rounded-xl border border-surface-border bg-surface-subtle p-1">
                        <button
                          type="button"
                          onClick={() => {
                            setOfferType('product');
                            setUrgencyCopy(DEFAULT_PRODUCT_TEMPLATES.urgency);
                            setLookCopy(DEFAULT_PRODUCT_TEMPLATES.look);
                            setMinimalistCopy(
                              DEFAULT_PRODUCT_TEMPLATES.minimalist
                            );
                            setActiveCopyText(
                              DEFAULT_PRODUCT_TEMPLATES[selectedVariation]
                            );
                          }}
                          className={`inline-flex cursor-pointer items-center gap-1.5 rounded-lg px-3 py-1 text-xs font-medium transition-colors ${
                            offerType === 'product'
                              ? 'shadow-xs bg-surface-panel font-semibold text-content-main'
                              : 'text-content-muted hover:text-content-main'
                          }`}
                        >
                          <Tag className="h-3.5 w-3.5 text-brand-500" />
                          Produto
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setOfferType('coupon');
                            setUrgencyCopy(DEFAULT_COUPON_TEMPLATES.urgency);
                            setLookCopy(DEFAULT_COUPON_TEMPLATES.look);
                            setMinimalistCopy(
                              DEFAULT_COUPON_TEMPLATES.minimalist
                            );
                            setActiveCopyText(
                              DEFAULT_COUPON_TEMPLATES[selectedVariation]
                            );
                          }}
                          className={`inline-flex cursor-pointer items-center gap-1.5 rounded-lg px-3 py-1 text-xs font-medium transition-colors ${
                            offerType === 'coupon'
                              ? 'shadow-xs bg-surface-panel font-semibold text-content-main'
                              : 'text-content-muted hover:text-content-main'
                          }`}
                        >
                          <Ticket className="h-3.5 w-3.5 text-brand-500" />
                          Cupom
                        </button>
                      </div>
                    </div>

                    {/* Input de URL e Botão de Extração */}
                    <div className="space-y-2">
                      <label className="block text-xs font-semibold text-content-main">
                        {offerType === 'product'
                          ? 'Link do Produto (Shopee, Amazon, ML, Magalu)'
                          : 'Link da Loja / Campanha de Cupom'}
                      </label>
                      <div className="flex gap-2">
                        <div className="flex-1">
                          <Input
                            placeholder="Cole o link ou texto da promoção (com ou sem https, ex: shopee.com.br/...)"
                            value={urlInput}
                            error={urlInputError || undefined}
                            onChange={(e) => {
                              setUrlInput(e.target.value);
                              if (urlInputError) setUrlInputError(null);
                            }}
                          />
                        </div>
                        <Button
                          variant="primary"
                          isLoading={isScraping}
                          onClick={handleScrapeUrl}
                        >
                          Extrair
                        </Button>
                      </div>
                    </div>

                    {/* Feedback Sucinto da Extração */}
                    {extractionResult && (
                      <div
                        className={`flex items-center justify-between gap-3 rounded-lg border p-2.5 text-xs transition-all ${
                          extractionResult.status === 'success'
                            ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-900 dark:border-emerald-500/20 dark:bg-emerald-500/5 dark:text-emerald-300'
                            : extractionResult.status === 'warning'
                              ? 'border-amber-500/30 bg-amber-500/10 text-amber-900 dark:border-amber-500/20 dark:bg-amber-500/5 dark:text-amber-300'
                              : 'border-red-500/30 bg-red-500/10 text-red-900 dark:border-red-500/20 dark:bg-red-500/5 dark:text-red-300'
                        }`}
                      >
                        <div className="flex min-w-0 items-center gap-2">
                          {extractionResult.status === 'success' ? (
                            <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-600 dark:text-emerald-400" />
                          ) : extractionResult.status === 'warning' ? (
                            <AlertTriangle className="h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" />
                          ) : (
                            <AlertCircle className="h-4 w-4 shrink-0 text-red-600 dark:text-red-400" />
                          )}
                          <p className="text-xs font-medium leading-tight">
                            {extractionResult.message}
                          </p>
                        </div>

                        <div className="flex shrink-0 items-center gap-1.5">
                          {extractionResult.actionRequired ===
                            'configure_marketplace' && (
                            <Button
                              variant="secondary"
                              size="sm"
                              leftIcon={<SettingsIcon className="h-3 w-3" />}
                              onClick={() => {
                                window.history.pushState(null, '', '/settings');
                                window.dispatchEvent(
                                  new PopStateEvent('popstate')
                                );
                              }}
                              className="px-2 py-0.5 text-micro"
                            >
                              Configurar Loja
                            </Button>
                          )}
                          <button
                            type="button"
                            onClick={() => setExtractionResult(null)}
                            className="cursor-pointer rounded p-1 text-content-muted hover:bg-surface-subtle hover:text-content-main"
                            title="Dispensar aviso"
                            aria-label="Dispensar aviso"
                          >
                            <X className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </div>
                    )}
                  </Card.Body>
                </Card>

                {/* Metadados e Valores */}
                <Card>
                  <Card.Header
                    title="Detalhes da Oferta"
                    description="Revise as informações extraídas ou adicione manualmente"
                  />
                  <Card.Body className="space-y-4">
                    <Input
                      label="Título da Oferta"
                      placeholder="Ex: Fritadeira Airfryer 4L Inox"
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                    />

                    {offerType === 'product' ? (
                      <>
                        <Input
                          label="URL da Imagem do Produto"
                          placeholder="https://..."
                          value={imageUrl}
                          onChange={(e) => setImageUrl(e.target.value)}
                        />

                        <div className="grid grid-cols-2 gap-3">
                          <Input
                            label="Preço Original (R$)"
                            placeholder="Ex: 299.90"
                            type="number"
                            step="0.01"
                            value={originalPrice}
                            onChange={(e) => setOriginalPrice(e.target.value)}
                          />
                          <Input
                            label="Preço Promocional (R$)"
                            placeholder="Ex: 199.90"
                            type="number"
                            step="0.01"
                            value={promotionalPrice}
                            onChange={(e) =>
                              setPromotionalPrice(e.target.value)
                            }
                          />
                        </div>

                        {/* Cupom Opcional Vinculado ao Produto */}
                        <div className="bg-surface-subtle/60 space-y-2.5 rounded-xl border border-surface-border p-3">
                          <label className="flex cursor-pointer items-center justify-between">
                            <div className="space-y-0.5">
                              <span className="flex items-center gap-1.5 text-xs font-semibold text-content-main">
                                <Ticket className="h-3.5 w-3.5 text-brand-500" />
                                Vincular Cupom ou Desconto Extra (Opcional)
                              </span>
                              <span className="block text-2xs text-content-muted">
                                Adiciona cupom e link de ativação à mensagem do
                                produto
                              </span>
                            </div>
                            <input
                              type="checkbox"
                              checked={hasProductCoupon}
                              onChange={(e) => {
                                setHasProductCoupon(e.target.checked);
                                if (!e.target.checked) {
                                  setCouponCode('');
                                  setCouponLink('');
                                }
                              }}
                              className="h-4 w-4 cursor-pointer rounded border-surface-border text-brand-600 focus:ring-brand-500"
                            />
                          </label>

                          {hasProductCoupon && (
                            <div className="border-surface-border/60 grid grid-cols-1 gap-2.5 border-t pt-2 sm:grid-cols-2">
                              <Input
                                label="Código do Cupom"
                                placeholder="Ex: 1010SHO"
                                value={couponCode}
                                onChange={(e) => setCouponCode(e.target.value)}
                              />
                              <Input
                                label="Link de Resgate do Cupom (Opcional)"
                                placeholder="https://shopee-app.com/... ou https://..."
                                value={couponLink}
                                onChange={(e) => setCouponLink(e.target.value)}
                                helperText="Exibe bloco de ativação do cupom na mensagem"
                              />
                            </div>
                          )}
                        </div>
                      </>
                    ) : (
                      <div className="space-y-4">
                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                          <Input
                            label="Código do Cupom"
                            placeholder="Ex: OFF50"
                            value={couponCode}
                            onChange={(e) => setCouponCode(e.target.value)}
                          />
                          <Input
                            label="Regras ou Validade"
                            placeholder="Ex: Acima de R$ 200, válido até amanhã"
                            value={couponRules}
                            onChange={(e) => setCouponRules(e.target.value)}
                          />
                        </div>
                        <Input
                          label="Link de Ativação / Resgate do Cupom"
                          placeholder="Ex: https://shopee-app.com/8fSkKKuoQK"
                          value={couponLink}
                          onChange={(e) => setCouponLink(e.target.value)}
                          helperText="Link direto para resgate do cupom antes de ir ao carrinho"
                        />
                      </div>
                    )}

                    {/* Link de Afiliado */}
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <label className="block text-xs font-semibold text-content-main">
                          Link de Afiliado
                        </label>
                        <div className="flex items-center gap-1.5">
                          {marketplace !== 'other' && (
                            <Badge
                              variant="neutral"
                              className="font-mono text-micro uppercase"
                            >
                              {marketplace}
                            </Badge>
                          )}
                          {affiliateUrl ? (
                            <Badge
                              variant={isShortLink ? 'online' : 'info'}
                              className="text-micro"
                            >
                              {isShortLink
                                ? 'Link Curto Oficial'
                                : 'Afiliação Ativa'}
                            </Badge>
                          ) : marketplace !== 'other' ? (
                            <Badge variant="warning" className="text-micro">
                              Sem Afiliação
                            </Badge>
                          ) : null}
                        </div>
                      </div>

                      <Input
                        placeholder="Link de afiliado gerado automaticamente ou cole seu próprio link..."
                        value={affiliateUrl}
                        onChange={(e) => setAffiliateUrl(e.target.value)}
                        helperText={
                          affiliateUrl
                            ? 'Os membros dos grupos serão direcionados para este link de afiliado monetizado.'
                            : 'Se vazio, o link original do produto será utilizado.'
                        }
                      />
                    </div>

                    {/* Formato do Link na Mensagem do WhatsApp */}
                    <div className="space-y-1.5 rounded-xl border border-surface-border bg-surface-subtle p-3">
                      <div className="flex items-center justify-between">
                        <label className="block text-xs font-semibold text-content-main">
                          Formato do Link na Mensagem do WhatsApp
                        </label>
                        <Badge variant="neutral" className="text-micro">
                          {linkType === 'api'
                            ? 'API Rastreável'
                            : 'Link Nativo'}
                        </Badge>
                      </div>
                      <p className="text-2xs text-content-muted">
                        Escolha se a mensagem levará o link curto da API (com
                        telemetria e troca remota) ou o link direto do
                        marketplace.
                      </p>
                      <div className="grid grid-cols-1 gap-2 pt-1 sm:grid-cols-2">
                        <button
                          type="button"
                          onClick={() => handleSelectLinkType('api')}
                          className={`cursor-pointer rounded-lg border p-2.5 text-left transition-all ${
                            linkType === 'api'
                              ? 'border-brand-500 bg-brand-500/10 font-medium text-brand-600 dark:text-brand-400'
                              : 'border-surface-border bg-surface-panel text-content-muted hover:text-content-main'
                          }`}
                        >
                          <div className="flex items-center justify-between text-xs font-semibold">
                            <span>Link Rastreável da API</span>
                            {linkType === 'api' && (
                              <Check className="h-3.5 w-3.5" />
                            )}
                          </div>
                          <span className="mt-1 block text-2xs opacity-80">
                            /{'{tenant}'}/{'{loja}'}/{'{code}'} — cliques
                            monitorados
                          </span>
                        </button>

                        <button
                          type="button"
                          onClick={() => handleSelectLinkType('marketplace')}
                          className={`cursor-pointer rounded-lg border p-2.5 text-left transition-all ${
                            linkType === 'marketplace'
                              ? 'border-brand-500 bg-brand-500/10 font-medium text-brand-600 dark:text-brand-400'
                              : 'border-surface-border bg-surface-panel text-content-muted hover:text-content-main'
                          }`}
                        >
                          <div className="flex items-center justify-between text-xs font-semibold">
                            <span>Link Nativo da Loja</span>
                            {linkType === 'marketplace' && (
                              <Check className="h-3.5 w-3.5" />
                            )}
                          </div>
                          <span className="mt-1 block text-2xs opacity-80">
                            {marketplace !== 'other'
                              ? `${marketplace}.com...`
                              : 'Link direto do vendedor'}
                          </span>
                        </button>
                      </div>
                    </div>

                    {/* Multiseleção de Categorias */}
                    <div className="space-y-1.5">
                      <label className="block text-xs font-semibold text-content-main">
                        Categorias
                      </label>
                      <div className="flex flex-wrap gap-1.5">
                        {AVAILABLE_CATEGORIES.map((cat) => {
                          const isSelected = selectedCategories.includes(cat);
                          return (
                            <button
                              key={cat}
                              type="button"
                              onClick={() => toggleCategory(cat)}
                              className={`cursor-pointer rounded-lg border px-2.5 py-1 text-xs font-medium transition-colors ${
                                isSelected
                                  ? 'border-brand-500 bg-brand-500/10 text-brand-600 dark:text-brand-400'
                                  : 'border-surface-border bg-surface-subtle text-content-muted hover:text-content-main'
                              }`}
                            >
                              {cat}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {/* Canais de Destino da Oferta */}
                    <div className="space-y-3 rounded-xl border border-surface-border bg-surface-subtle p-3.5">
                      <div className="space-y-0.5">
                        <span className="block text-xs font-semibold text-content-main">
                          Canais de Destino da Oferta
                        </span>
                        <p className="text-2xs text-content-muted">
                          Escolha para onde esta oferta deve ser distribuída
                        </p>
                      </div>

                      <div className="space-y-2">
                        <label className="flex cursor-pointer items-center justify-between rounded-lg border border-surface-border bg-surface-panel p-2.5 hover:bg-surface-subtle">
                          <div className="space-y-0.5">
                            <span className="block text-xs font-semibold text-content-main">
                              Disparar nos Grupos de WhatsApp
                            </span>
                            <span className="block text-2xs text-content-muted">
                              Enfileira a mensagem para envio automático nos
                              grupos selecionados abaixo.
                            </span>
                          </div>
                          <input
                            type="checkbox"
                            checked={sendToWhatsApp}
                            onChange={(e) =>
                              setSendToWhatsApp(e.target.checked)
                            }
                            className="h-4 w-4 cursor-pointer rounded border-surface-border text-brand-600 focus:ring-brand-500"
                          />
                        </label>

                        <label className="flex cursor-pointer items-center justify-between rounded-lg border border-surface-border bg-surface-panel p-2.5 hover:bg-surface-subtle">
                          <div className="space-y-0.5">
                            <span className="block text-xs font-semibold text-content-main">
                              Publicar na Vitrine Pública de Ofertas
                            </span>
                            <span className="block text-2xs text-content-muted">
                              Disponibiliza a oferta na vitrine pública do seu
                              catálogo.
                            </span>
                          </div>
                          <input
                            type="checkbox"
                            checked={showInShowcase}
                            onChange={(e) =>
                              setShowInShowcase(e.target.checked)
                            }
                            className="h-4 w-4 cursor-pointer rounded border-surface-border text-brand-600 focus:ring-brand-500"
                          />
                        </label>
                      </div>
                    </div>
                  </Card.Body>
                </Card>

                {/* Painel de Geração de Abordagens */}
                <Card className={!urlInput.trim() ? 'opacity-60' : undefined}>
                  <Card.Header
                    title="Sugestão Automática de Mensagem"
                    description="Gere 3 abordagens persuasivas estruturadas para o WhatsApp"
                    action={
                      <Button
                        variant="primary"
                        size="sm"
                        leftIcon={<Sparkles className="h-3.5 w-3.5" />}
                        isLoading={isGeneratingCopy}
                        disabled={!urlInput.trim() || isGeneratingCopy}
                        title={
                          !urlInput.trim()
                            ? 'Preencha o link da oferta para liberar a geração de copy'
                            : 'Gerar abordagens persuasivas'
                        }
                        onClick={() => handleGenerateCopy()}
                      >
                        Gerar Sugestões
                      </Button>
                    }
                  />
                  <Card.Body className="space-y-4">
                    {/* Seletor de Abordagens (Sem ícones externos, sem 'pronta' ou 'aguardando') */}
                    <div className="grid grid-cols-3 gap-2">
                      {[
                        {
                          id: 'urgency',
                          label: 'Urgência',
                          hasContent: Boolean(urgencyCopy),
                        },
                        {
                          id: 'look',
                          label: 'Achadinho',
                          hasContent: Boolean(lookCopy),
                        },
                        {
                          id: 'minimalist',
                          label: 'Minimalista',
                          hasContent: Boolean(minimalistCopy),
                        },
                      ].map((tab) => (
                        <button
                          key={tab.id}
                          type="button"
                          onClick={() => {
                            setSelectedVariation(
                              tab.id as typeof selectedVariation
                            );
                            if (tab.id === 'urgency')
                              setActiveCopyText(urgencyCopy);
                            if (tab.id === 'look') setActiveCopyText(lookCopy);
                            if (tab.id === 'minimalist')
                              setActiveCopyText(minimalistCopy);
                          }}
                          className={`flex cursor-pointer items-center justify-center rounded-xl border px-3 py-2.5 text-center transition-all ${
                            selectedVariation === tab.id
                              ? 'shadow-xs border-brand-500 bg-brand-500/10 font-bold text-brand-600 dark:text-brand-400'
                              : 'border-surface-border bg-surface-subtle text-content-main hover:border-surface-border'
                          }`}
                        >
                          <span className="text-xs font-semibold">
                            {tab.label}
                          </span>
                        </button>
                      ))}
                    </div>

                    {/* Editor da Copy Ativa com Ferramentas de Estilização WhatsApp */}
                    <div className="space-y-2">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <label className="text-xs font-semibold text-content-main">
                          Texto da Mensagem (Editável)
                        </label>

                        {/* Barra de Ferramentas de Estilização WhatsApp */}
                        <div className="flex items-center gap-1 rounded-lg border border-surface-border bg-surface-subtle p-1">
                          <button
                            type="button"
                            disabled={!urlInput.trim()}
                            onClick={() => handleInsertFormatting('*')}
                            title="Negrito (*texto*)"
                            className="cursor-pointer rounded p-1 text-content-muted hover:bg-surface-panel hover:text-content-main disabled:opacity-40"
                          >
                            <Bold className="h-3.5 w-3.5" />
                          </button>
                          <button
                            type="button"
                            disabled={!urlInput.trim()}
                            onClick={() => handleInsertFormatting('_')}
                            title="Itálico (_texto_)"
                            className="cursor-pointer rounded p-1 text-content-muted hover:bg-surface-panel hover:text-content-main disabled:opacity-40"
                          >
                            <Italic className="h-3.5 w-3.5" />
                          </button>
                          <button
                            type="button"
                            disabled={!urlInput.trim()}
                            onClick={() => handleInsertFormatting('~')}
                            title="Tachado (~texto~)"
                            className="cursor-pointer rounded p-1 text-content-muted hover:bg-surface-panel hover:text-content-main disabled:opacity-40"
                          >
                            <Strikethrough className="h-3.5 w-3.5" />
                          </button>
                          <button
                            type="button"
                            disabled={!urlInput.trim()}
                            onClick={() => handleInsertFormatting('```')}
                            title="Monoespaçado (```texto```)"
                            className="cursor-pointer rounded p-1 text-content-muted hover:bg-surface-panel hover:text-content-main disabled:opacity-40"
                          >
                            <Code className="h-3.5 w-3.5" />
                          </button>
                          <div className="mx-0.5 h-3.5 w-px bg-surface-border" />
                          <button
                            type="button"
                            disabled={!urlInput.trim()}
                            onClick={() => handleInsertFormatting('🔥 ', '')}
                            title="Emoji de Destaque (🔥)"
                            className="cursor-pointer rounded px-1.5 py-0.5 text-xs text-content-muted hover:bg-surface-panel hover:text-content-main disabled:opacity-40"
                          >
                            🔥
                          </button>
                          <button
                            type="button"
                            disabled={!urlInput.trim()}
                            onClick={() => handleInsertFormatting('⚡ ', '')}
                            title="Emoji de Relâmpago (⚡)"
                            className="cursor-pointer rounded px-1.5 py-0.5 text-xs text-content-muted hover:bg-surface-panel hover:text-content-main disabled:opacity-40"
                          >
                            ⚡
                          </button>
                          <button
                            type="button"
                            disabled={!urlInput.trim()}
                            onClick={() => handleInsertFormatting('👇 ', '')}
                            title="Emoji de Apontador (👇)"
                            className="cursor-pointer rounded px-1.5 py-0.5 text-xs text-content-muted hover:bg-surface-panel hover:text-content-main disabled:opacity-40"
                          >
                            👇
                          </button>
                        </div>
                      </div>

                      {/* Variáveis Dinâmicas de Template (Colapsável) */}
                      <div className="border-surface-border/80 bg-surface-subtle/50 rounded-lg border transition-all">
                        <div className="flex items-center justify-between px-2.5 py-1.5">
                          <button
                            type="button"
                            onClick={() =>
                              setIsVariablesExpanded(!isVariablesExpanded)
                            }
                            className="flex cursor-pointer items-center gap-1.5 text-micro font-medium text-content-muted hover:text-content-main"
                          >
                            {isVariablesExpanded ? (
                              <ChevronDown className="h-3.5 w-3.5" />
                            ) : (
                              <ChevronRight className="h-3.5 w-3.5" />
                            )}
                            <span>Variáveis de Template</span>
                            <span className="py-0.2 rounded bg-surface-panel px-1.5 text-2xs text-content-muted">
                              {isVariablesExpanded ? 'Ocultar' : 'Exibir tags'}
                            </span>
                          </button>

                          {/* Botão de Restaurar Template Padrão */}
                          <button
                            type="button"
                            onClick={() => {
                              setActiveCopyText(
                                offerType === 'product'
                                  ? DEFAULT_PRODUCT_TEMPLATE
                                  : DEFAULT_COUPON_TEMPLATE
                              );
                            }}
                            title="Restaurar template inicial com variáveis"
                            className="cursor-pointer text-2xs text-content-muted underline hover:text-brand-500"
                          >
                            Restaurar padrão
                          </button>
                        </div>

                        {isVariablesExpanded && (
                          <div className="border-surface-border/60 flex flex-wrap items-center gap-1.5 border-t px-2.5 py-2">
                            {[
                              { tag: '{{nome_produto}}', label: 'Nome' },
                              { tag: '{{preco}}', label: 'Preço' },
                              { tag: '{{preco_original}}', label: 'Preço De' },
                              { tag: '{{desconto}}', label: 'Desconto' },
                              { tag: '{{cupom}}', label: 'Cupom' },
                              { tag: '{{bloco_cupom}}', label: 'Bloco Cupom' },
                              { tag: '{{link_cupom}}', label: 'Link Cupom' },
                              { tag: '{{link_url}}', label: 'Link' },
                            ].map((item) => (
                              <button
                                key={item.tag}
                                type="button"
                                onClick={() => handleInsertTag(item.tag)}
                                title={`Inserir ${item.tag} no cursor`}
                                className="cursor-pointer rounded border border-surface-border bg-surface-panel px-2 py-0.5 font-mono text-micro text-content-main transition-colors hover:border-brand-500 hover:text-brand-500"
                              >
                                + {item.tag}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Ganchos Contextuais Inteligentes */}
                      {recommendedHooks.length > 0 && (
                        <div className="bg-surface-subtle/70 space-y-1.5 rounded-lg border border-surface-border p-2.5">
                          <div className="flex items-center justify-between">
                            <span className="flex items-center gap-1.5 text-2xs font-semibold text-content-main">
                              <Sparkles className="h-3 w-3 text-amber-500" />
                              Ganchos Sugeridos para esta Oferta (1 clique para
                              aplicar)
                            </span>
                            <span className="text-micro text-content-muted">
                              Substitui a chamada inicial
                            </span>
                          </div>
                          <div className="flex flex-wrap gap-1.5">
                            {recommendedHooks.map((hook, idx) => (
                              <button
                                key={idx}
                                type="button"
                                onClick={() => handleApplyHook(hook.text)}
                                className="group flex cursor-pointer items-center gap-1 rounded-md border border-surface-border bg-surface-panel px-2 py-1 text-left text-2xs text-content-main transition-all hover:border-brand-500 hover:bg-brand-500/10 hover:text-brand-600 dark:hover:text-brand-400"
                                title={`Aplicar: "${hook.text}"`}
                              >
                                <span className="font-semibold text-content-muted group-hover:text-brand-500">
                                  [{hook.label}]
                                </span>
                                <span>{hook.text}</span>
                              </button>
                            ))}
                          </div>
                        </div>
                      )}

                      <textarea
                        ref={copyTextareaRef}
                        rows={6}
                        value={activeCopyText}
                        onChange={(e) => setActiveCopyText(e.target.value)}
                        placeholder="O texto da mensagem gerado ou digitado aparecerá aqui. Você pode usar variáveis {{nome_produto}}, {{preco}}, *negrito*, _itálico_, ~tachado~ e emojis."
                        className="w-full rounded-xl border border-surface-border bg-surface-subtle p-3 font-mono text-xs leading-relaxed text-content-main focus:border-brand-500 focus:outline-none"
                      />
                    </div>
                  </Card.Body>
                </Card>

                {/* Grupos de Destino para Disparo */}
                <Card>
                  <Card.Header
                    title="Grupos de Destino"
                    description="Selecione quais grupos receberão este disparo quando enviado para a fila"
                  />
                  <Card.Body className="space-y-3">
                    {availableGroups.filter((g) => g.status === 'active')
                      .length === 0 ? (
                      <div className="rounded-xl border border-amber-500/20 bg-amber-500/5 p-3.5 text-xs text-amber-700 dark:text-amber-400">
                        <div className="flex items-start gap-2.5">
                          <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
                          <div>
                            <span className="font-semibold">
                              Nenhum grupo ativo encontrado:
                            </span>{' '}
                            <span>
                              Para disparar ofertas, você precisa cadastrar ao
                              menos um Grupo com status Ativo na aba de Grupos.
                              Por enquanto, a oferta só poderá ser salva em
                              Aguardando.
                            </span>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        <label className="flex cursor-pointer items-center gap-2.5 rounded-xl border border-surface-border bg-surface-subtle p-3 transition-colors hover:bg-surface-panel">
                          <input
                            type="checkbox"
                            checked={sendToAllActive}
                            onChange={(e) => {
                              setSendToAllActive(e.target.checked);
                              if (e.target.checked) {
                                setTargetGroupIds([]);
                              } else {
                                setTargetGroupIds(
                                  availableGroups
                                    .filter((g) => g.status === 'active')
                                    .map((g) => g.id)
                                );
                              }
                            }}
                            className="h-4 w-4 rounded border-surface-border text-brand-500 focus:ring-brand-500"
                          />
                          <div className="flex-1">
                            <span className="block text-xs font-semibold text-content-main">
                              Enviar para todos os grupos ativos
                            </span>
                            <span className="block text-2xs text-content-muted">
                              A mensagem será programada para todos os{' '}
                              {
                                availableGroups.filter(
                                  (g) => g.status === 'active'
                                ).length
                              }{' '}
                              grupos ativos atuais
                            </span>
                          </div>
                        </label>

                        {!sendToAllActive && (
                          <div className="space-y-1.5 pt-1">
                            <span className="block text-xs font-semibold text-content-main">
                              Selecione os grupos específicos:
                            </span>
                            <div className="max-h-48 space-y-1.5 overflow-y-auto rounded-xl border border-surface-border p-2">
                              {availableGroups
                                .filter((g) => g.status === 'active')
                                .map((group) => {
                                  const isChecked = targetGroupIds.includes(
                                    group.id
                                  );
                                  return (
                                    <label
                                      key={group.id}
                                      className="flex cursor-pointer items-center justify-between rounded-lg px-2.5 py-1.5 hover:bg-surface-subtle"
                                    >
                                      <div className="flex items-center gap-2">
                                        <input
                                          type="checkbox"
                                          checked={isChecked}
                                          onChange={() => {
                                            if (isChecked) {
                                              setTargetGroupIds(
                                                targetGroupIds.filter(
                                                  (id) => id !== group.id
                                                )
                                              );
                                            } else {
                                              setTargetGroupIds([
                                                ...targetGroupIds,
                                                group.id,
                                              ]);
                                            }
                                          }}
                                          className="h-3.5 w-3.5 rounded border-surface-border text-brand-500 focus:ring-brand-500"
                                        />
                                        <span className="text-xs font-medium text-content-main">
                                          {group.name}
                                        </span>
                                      </div>
                                      <span className="font-mono text-micro text-content-muted">
                                        {group.current_members} membros
                                      </span>
                                    </label>
                                  );
                                })}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </Card.Body>
                </Card>
              </div>

              {/* Barra Inferior Fixa de Ações (Bottom Sticky Bar) */}
              <div className="sticky bottom-0 z-30 col-span-12 -mx-4 -mb-6 mt-4 flex items-center justify-between gap-3 border-t border-surface-border bg-surface-panel px-6 py-3.5 shadow-lg sm:-mx-6 sm:-mb-8 lg:-mx-8">
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setIsPreviewExpanded(!isPreviewExpanded)}
                    leftIcon={<Eye className="h-3.5 w-3.5" />}
                    className="text-content-muted hover:text-content-main"
                  >
                    {isPreviewExpanded
                      ? 'Ocultar Preview'
                      : 'Exibir Preview WhatsApp'}
                  </Button>
                </div>

                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      resetForm();
                      setViewMode('list');
                    }}
                    disabled={isSaving}
                  >
                    Cancelar
                  </Button>

                  {editingOfferStatus === 'sent' ? (
                    <>
                      <Button
                        variant="secondary"
                        size="sm"
                        isLoading={isSaving}
                        disabled={isSaving}
                        title="Salva alterações na oferta enviada sem enfileirar novamente"
                        onClick={() => handleSaveOffer('pending')}
                      >
                        Salvar Alterações (Hotfix)
                      </Button>
                      <Button
                        variant="primary"
                        size="sm"
                        isLoading={isSaving}
                        disabled={isSaving}
                        leftIcon={<Send className="h-3.5 w-3.5" />}
                        title="Salva alterações e reenvia a oferta para a fila de disparo"
                        onClick={() => handleSaveOffer('queued')}
                      >
                        Reenviar para a Fila
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button
                        variant="secondary"
                        size="sm"
                        isLoading={isSaving}
                        onClick={() => handleSaveOffer('pending')}
                      >
                        Salvar em Aguardando
                      </Button>
                      <Button
                        variant="primary"
                        size="sm"
                        isLoading={isSaving}
                        disabled={isSaving}
                        title="Valida os dados e enfileira a oferta para envio aos grupos"
                        onClick={() => handleSaveOffer('queued')}
                      >
                        Salvar na Fila
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </div>
          ) : (
            /* 4. Modo Listagem Geral de Promoções */
            <div className="space-y-4">
              <div className="flex flex-col justify-between gap-3 sm:flex-row sm:items-center">
                {/* Abas de Status com Contadores */}
                <div className="flex items-center gap-1 overflow-x-auto rounded-xl border border-surface-border bg-surface-subtle p-1">
                  {[
                    { id: 'all', label: 'Todas', count: metrics.total_offers },
                    {
                      id: 'pending',
                      label: 'Aguardando',
                      count: metrics.pending_offers,
                    },
                    {
                      id: 'queued',
                      label: 'Na Fila',
                      count: metrics.queued_offers,
                    },
                    {
                      id: 'error',
                      label: 'Com Falha',
                      count: metrics.error_offers,
                    },
                    {
                      id: 'sent',
                      label: 'Enviadas',
                      count: metrics.sent_offers,
                    },
                    {
                      id: 'archived',
                      label: 'Arquivadas',
                      count: metrics.archived_offers,
                    },
                  ].map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => setStatusFilter(tab.id)}
                      className={`flex cursor-pointer items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium transition-colors ${
                        statusFilter === tab.id
                          ? 'shadow-xs bg-surface-panel font-semibold text-content-main'
                          : 'text-content-muted hover:text-content-main'
                      }`}
                    >
                      <span>{tab.label}</span>
                      {typeof tab.count === 'number' && tab.count > 0 && (
                        <span
                          className={`py-0.2 rounded-full px-1.5 text-micro font-bold ${
                            tab.id === 'error'
                              ? 'bg-red-500/10 text-red-600 dark:text-red-400'
                              : 'bg-surface-subtle text-content-muted'
                          }`}
                        >
                          {tab.count}
                        </span>
                      )}
                    </button>
                  ))}
                </div>

                {/* Filtro por Categoria (Multi-seleção) e Botão de Atualizar */}
                <div className="flex flex-wrap items-center gap-2">
                  <div className="flex items-center gap-1">
                    <select
                      value=""
                      onChange={(e) => {
                        const cat = e.target.value;
                        if (!cat) return;
                        if (cat === 'all') {
                          setSelectedCategoryFilters([]);
                        } else if (!selectedCategoryFilters.includes(cat)) {
                          setSelectedCategoryFilters([
                            ...selectedCategoryFilters,
                            cat,
                          ]);
                        }
                      }}
                      className="rounded-lg border border-surface-border bg-surface-panel px-2.5 py-1.5 text-xs font-medium text-content-main focus:border-brand-500 focus:outline-none"
                    >
                      <option value="">
                        {selectedCategoryFilters.length === 0
                          ? 'Todas as Categorias'
                          : `Categorias (${selectedCategoryFilters.length} ativas)`}
                      </option>
                      <option value="all">Limpar / Todas as Categorias</option>
                      {AVAILABLE_CATEGORIES.map((cat) => (
                        <option key={cat} value={cat}>
                          {selectedCategoryFilters.includes(cat)
                            ? `✓ ${cat}`
                            : cat}
                        </option>
                      ))}
                    </select>

                    {selectedCategoryFilters.length > 0 && (
                      <button
                        type="button"
                        onClick={() => setSelectedCategoryFilters([])}
                        className="cursor-pointer rounded-md bg-surface-subtle px-2 py-1 text-2xs font-semibold text-content-muted hover:text-content-main"
                        title="Limpar filtros de categoria"
                      >
                        Limpar
                      </button>
                    )}
                  </div>

                  <Button
                    variant="secondary"
                    size="sm"
                    isLoading={isLoading}
                    leftIcon={<RefreshCw className="h-3.5 w-3.5" />}
                    onClick={loadData}
                  >
                    Atualizar
                  </Button>
                </div>
              </div>

              {/* Filtros Rápidos (Ticket 06) */}
              <div className="flex flex-wrap items-center gap-2 pt-1">
                <span className="text-2xs font-semibold text-content-muted">
                  Filtros Rápidos:
                </span>
                <button
                  type="button"
                  onClick={() =>
                    setFilterMinRating((prev) => (prev === 4.8 ? null : 4.8))
                  }
                  className={`flex cursor-pointer items-center gap-1.5 rounded-lg border px-2.5 py-1 text-xs font-medium transition-colors ${
                    filterMinRating === 4.8
                      ? 'border-brand-500 bg-brand-500/10 font-semibold text-brand-600 dark:text-brand-400'
                      : 'hover:border-surface-border/80 border-surface-border bg-surface-panel text-content-muted hover:text-content-main'
                  }`}
                >
                  <Star className="h-3.5 w-3.5 text-amber-500" />
                  <span>Nota 4.8+</span>
                </button>

                <button
                  type="button"
                  onClick={() =>
                    setFilterMinCommission((prev) => (prev === 10 ? null : 10))
                  }
                  className={`flex cursor-pointer items-center gap-1.5 rounded-lg border px-2.5 py-1 text-xs font-medium transition-colors ${
                    filterMinCommission === 10
                      ? 'border-brand-500 bg-brand-500/10 font-semibold text-brand-600 dark:text-brand-400'
                      : 'hover:border-surface-border/80 border-surface-border bg-surface-panel text-content-muted hover:text-content-main'
                  }`}
                >
                  <span>💰 Comissão &gt; 10%</span>
                </button>

                <button
                  type="button"
                  onClick={() => setFilterIsFull((prev) => !prev)}
                  className={`flex cursor-pointer items-center gap-1.5 rounded-lg border px-2.5 py-1 text-xs font-medium transition-colors ${
                    filterIsFull
                      ? 'border-brand-500 bg-brand-500/10 font-semibold text-brand-600 dark:text-brand-400'
                      : 'hover:border-surface-border/80 border-surface-border bg-surface-panel text-content-muted hover:text-content-main'
                  }`}
                >
                  <Zap className="h-3.5 w-3.5 text-emerald-500" />
                  <span>Envio Full</span>
                </button>

                {(filterMinRating !== null ||
                  filterMinCommission !== null ||
                  filterIsFull) && (
                  <button
                    type="button"
                    onClick={() => {
                      setFilterMinRating(null);
                      setFilterMinCommission(null);
                      setFilterIsFull(false);
                    }}
                    className="cursor-pointer text-2xs font-semibold text-content-muted hover:text-content-main"
                  >
                    Limpar filtros rápidos
                  </button>
                )}
              </div>

              {/* Tags de Categorias Selecionadas no Filtro */}
              {selectedCategoryFilters.length > 0 && (
                <div className="flex flex-wrap items-center gap-1.5">
                  <span className="text-2xs font-medium text-content-muted">
                    Filtros ativos:
                  </span>
                  {selectedCategoryFilters.map((cat) => (
                    <span
                      key={cat}
                      className="flex items-center gap-1 rounded-md border border-brand-500/30 bg-brand-500/10 px-2 py-0.5 font-mono text-2xs font-semibold text-brand-600 dark:text-brand-400"
                    >
                      <span>{cat}</span>
                      <button
                        type="button"
                        onClick={() =>
                          setSelectedCategoryFilters(
                            selectedCategoryFilters.filter((c) => c !== cat)
                          )
                        }
                        className="cursor-pointer hover:text-content-main"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              )}

              {/* Barra de Ações em Lote (Bulk Actions) */}
              {selectedOfferIds.size > 0 && (
                <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-brand-500/30 bg-brand-500/10 p-3">
                  <div className="flex min-w-0 flex-wrap items-center gap-2">
                    <CheckSquare className="h-4 w-4 flex-shrink-0 text-brand-600 dark:text-brand-400" />
                    <span className="text-xs font-bold text-content-main">
                      {selectedOfferIds.size}{' '}
                      {selectedOfferIds.size === 1
                        ? 'oferta selecionada'
                        : 'ofertas selecionadas'}
                    </span>
                    {hasIncompleteDraftSelected && (
                      <Badge variant="warning" className="text-micro font-bold">
                        Contém rascunhos incompletos
                      </Badge>
                    )}
                  </div>

                  <div className="flex flex-wrap items-center gap-2">
                    {/* 1. Botão Enviar Selecionados para a Fila (Abre Drawer de Seleção de Fila) */}
                    {selectedOffersList.some((o) =>
                      [
                        'pending',
                        'draft',
                        'error',
                        'sent',
                        'archived',
                      ].includes(o.status)
                    ) && (
                      <Button
                        variant="primary"
                        size="sm"
                        disabled={hasIncompleteDraftSelected || isBulkActing}
                        isLoading={isBulkActing}
                        leftIcon={<Send className="h-3.5 w-3.5" />}
                        onClick={openQueueSelector}
                        title={
                          hasIncompleteDraftSelected
                            ? 'Rascunhos incompletos não podem ir para a fila'
                            : 'Enviar selecionados para a fila de disparo'
                        }
                      >
                        Enviar Selecionados para a Fila
                      </Button>
                    )}

                    {/* 2. Botão Pausar / Devolver para Aguardando (Visível quando há ofertas em queued ou error) */}
                    {selectedOffersList.some((o) =>
                      ['queued', 'error'].includes(o.status)
                    ) && (
                      <Button
                        variant="secondary"
                        size="sm"
                        disabled={isBulkActing}
                        isLoading={isBulkActing}
                        leftIcon={<Pause className="h-3.5 w-3.5" />}
                        onClick={() => handleBulkAction('pause')}
                        title="Pausar ofertas e devolver para Aguardando"
                      >
                        {statusFilter === 'error'
                          ? 'Devolver para Aguardando'
                          : 'Pausar Selecionadas'}
                      </Button>
                    )}

                    {/* 3. Botão Restaurar (Visível na aba Arquivadas ou quando há ofertas arquivadas) */}
                    {selectedOffersList.some(
                      (o) => o.status === 'archived'
                    ) && (
                      <Button
                        variant="secondary"
                        size="sm"
                        disabled={isBulkActing}
                        isLoading={isBulkActing}
                        leftIcon={<RotateCcw className="h-3.5 w-3.5" />}
                        onClick={() => handleBulkAction('pause')}
                        title="Restaurar ofertas arquivadas para Aguardando"
                      >
                        Restaurar para Aguardando
                      </Button>
                    )}

                    {/* 4. Botão Arquivar (Apenas se houver ofertas que não estejam arquivadas) */}
                    {selectedOffersList.some(
                      (o) => o.status !== 'archived'
                    ) && (
                      <Button
                        variant="secondary"
                        size="sm"
                        disabled={isBulkActing}
                        isLoading={isBulkActing}
                        leftIcon={<Archive className="h-3.5 w-3.5" />}
                        onClick={() => handleBulkAction('archive')}
                      >
                        Arquivar
                      </Button>
                    )}

                    {/* 5. Excluir (Disponível em todas as situações) */}
                    <Button
                      variant="danger"
                      size="sm"
                      disabled={isBulkActing}
                      isLoading={isBulkActing}
                      leftIcon={<Trash2 className="h-3.5 w-3.5" />}
                      onClick={() => handleBulkAction('delete')}
                    >
                      Excluir
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setSelectedOfferIds(new Set())}
                    >
                      Limpar Seleção
                    </Button>
                  </div>
                </div>
              )}

              {/* Estado de Carregamento, Erro ou Conteúdo */}
              {isLoading && offers.length === 0 ? (
                <LoadingSkeleton variant="table" count={5} />
              ) : loadError && offers.length === 0 ? (
                <Card>
                  <Card.Body className="mx-auto max-w-md space-y-4 px-4 py-12 text-center">
                    <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl border border-red-500/20 bg-red-500/10 text-red-600 dark:text-red-400">
                      <AlertCircle className="h-6 w-6" />
                    </div>
                    <div className="space-y-1">
                      <h4 className="text-sm font-semibold text-content-main">
                        Não foi possível recuperar os dados da API
                      </h4>
                      <p className="text-xs leading-relaxed text-content-muted">
                        Ocorreu uma falha ao carregar a lista de ofertas. Por
                        favor, verifique sua conexão ou tente novamente.
                      </p>
                    </div>
                    <Button
                      variant="primary"
                      size="sm"
                      leftIcon={<RefreshCw className="h-3.5 w-3.5" />}
                      onClick={loadData}
                    >
                      Tentar Novamente
                    </Button>
                  </Card.Body>
                </Card>
              ) : filteredAndSortedOffers.length === 0 && !isLoading ? (
                <Card>
                  <Card.Body className="mx-auto max-w-md space-y-4 px-4 py-12 text-center">
                    <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl border border-brand-500/20 bg-brand-500/10 text-brand-600 dark:text-brand-400">
                      <Flame className="h-6 w-6" />
                    </div>
                    <div className="space-y-1">
                      <h4 className="text-sm font-semibold text-content-main">
                        Nenhuma Oferta Encontrada
                      </h4>
                      <p className="text-xs leading-relaxed text-content-muted">
                        Cole links de produtos da Shopee, Amazon, Mercado Livre
                        ou Magalu para extrair metadados e gerar cópias
                        automáticas com a IA.
                      </p>
                    </div>
                    <Button
                      variant="primary"
                      size="sm"
                      leftIcon={<Plus className="h-3.5 w-3.5" />}
                      onClick={() => setViewMode('create')}
                    >
                      Criar Primeira Oferta
                    </Button>
                  </Card.Body>
                </Card>
              ) : (
                <DataTable
                  columns={columns}
                  data={filteredAndSortedOffers}
                  keyExtractor={(item) => item.id}
                  emptyMessage="Nenhuma oferta cadastrada com esse filtro."
                  pageSize={10}
                  onRowClick={(item) => openDrawer(item)}
                />
              )}
            </div>
          )}

          {/* 5. Drawer de Detalhamento & Gestão Completa da Oferta */}
          <Drawer
            isOpen={Boolean(selectedOfferForDrawer)}
            onClose={() => {
              setSelectedOfferForDrawer(null);
              setIsDrawerPreviewExpanded(false);
              setAuditPage(1);
            }}
            title={
              selectedOfferForDrawer ? (
                <div className="flex items-start gap-3">
                  {selectedOfferForDrawer.image_url ? (
                    <img
                      src={selectedOfferForDrawer.image_url}
                      alt=""
                      className="h-12 w-12 flex-shrink-0 rounded-lg border border-surface-border object-cover"
                    />
                  ) : (
                    <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-lg border border-surface-border bg-surface-subtle text-content-muted">
                      <Tag className="h-5 w-5" />
                    </div>
                  )}
                  <div className="min-w-0 flex-1 space-y-1">
                    <div className="flex items-center gap-2">
                      <Badge
                        variant={
                          selectedOfferForDrawer.status === 'sent'
                            ? 'online'
                            : selectedOfferForDrawer.status === 'queued'
                              ? 'warning'
                              : selectedOfferForDrawer.status === 'archived'
                                ? 'offline'
                                : 'neutral'
                        }
                        withDot
                        className="text-micro"
                      >
                        {selectedOfferForDrawer.status === 'sent'
                          ? 'Enviada'
                          : selectedOfferForDrawer.status === 'queued'
                            ? 'Na Fila'
                            : selectedOfferForDrawer.status === 'archived'
                              ? 'Arquivada'
                              : 'Aguardando'}
                      </Badge>
                      <span className="font-mono text-2xs font-bold text-content-muted">
                        #{selectedOfferForDrawer.short_code}
                      </span>
                    </div>
                    <h4 className="truncate text-xs font-semibold leading-snug text-content-main">
                      {selectedOfferForDrawer.title}
                    </h4>
                    <div className="flex flex-wrap items-center gap-2 pt-0.5">
                      <span className="font-mono text-2xs font-bold uppercase text-content-muted">
                        {selectedOfferForDrawer.marketplace}
                      </span>
                      {selectedOfferForDrawer.promotional_price && (
                        <span className="text-xs font-semibold text-content-main">
                          {`R$ ${selectedOfferForDrawer.promotional_price.toFixed(2).replace('.', ',')}`}
                        </span>
                      )}
                      {selectedOfferForDrawer.discount_percent && (
                        <Badge
                          variant="neutral"
                          className="text-micro font-semibold"
                        >
                          {`-${selectedOfferForDrawer.discount_percent}%`}
                        </Badge>
                      )}
                      {selectedOfferForDrawer.coupon_code && (
                        <Badge
                          variant="neutral"
                          className="font-mono text-micro"
                        >
                          Cupom: {selectedOfferForDrawer.coupon_code}
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
              ) : undefined
            }
            width="lg"
            footer={
              selectedOfferForDrawer ? (
                <div className="flex w-full flex-wrap items-center justify-between gap-2">
                  <div className="flex items-center gap-1.5">
                    {selectedOfferForDrawer.status !== 'archived' && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() =>
                          handleTransitionStatus(
                            selectedOfferForDrawer,
                            'archived'
                          )
                        }
                        title="Arquivar oferta"
                        className="text-content-muted hover:text-content-main"
                      >
                        <Archive className="h-3.5 w-3.5" />
                      </Button>
                    )}

                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setDeletingOffer(selectedOfferForDrawer);
                      }}
                      title="Excluir oferta"
                      className="text-content-muted hover:text-red-600 dark:hover:text-red-400"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>

                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setSelectedOfferForDrawer(null);
                        setIsDrawerPreviewExpanded(false);
                        setAuditPage(1);
                      }}
                      disabled={isUpdatingDrawer}
                    >
                      Fechar
                    </Button>

                    {/* Edição permitida para ofertas em Aguardando, Na Fila ou Enviadas */}
                    <Button
                      variant="secondary"
                      size="sm"
                      leftIcon={<Edit3 className="h-3.5 w-3.5" />}
                      onClick={() => handleEditOffer(selectedOfferForDrawer)}
                    >
                      Editar Oferta
                    </Button>

                    {selectedOfferForDrawer.status === 'pending' && (
                      <Button
                        variant="primary"
                        size="sm"
                        disabled={selectedOfferForDrawer.is_draft}
                        leftIcon={<Send className="h-3.5 w-3.5" />}
                        onClick={() =>
                          handleTransitionStatus(
                            selectedOfferForDrawer,
                            'queued'
                          )
                        }
                        title={
                          selectedOfferForDrawer.is_draft
                            ? 'Complete o cadastro antes de enviar para a fila'
                            : 'Colocar na fila de disparo'
                        }
                      >
                        Enviar para a Fila
                      </Button>
                    )}

                    {selectedOfferForDrawer.status === 'queued' && (
                      <Button
                        variant="secondary"
                        size="sm"
                        leftIcon={<Pause className="h-3.5 w-3.5" />}
                        onClick={() =>
                          handleTransitionStatus(
                            selectedOfferForDrawer,
                            'pending'
                          )
                        }
                        title="Pausar envio e devolver para Aguardando"
                      >
                        Pausar
                      </Button>
                    )}

                    {selectedOfferForDrawer.status === 'sent' && (
                      <Button
                        variant="primary"
                        size="sm"
                        leftIcon={<Send className="h-3.5 w-3.5" />}
                        onClick={() =>
                          handleTransitionStatus(
                            selectedOfferForDrawer,
                            'queued'
                          )
                        }
                        title="Reenviar oferta para a fila de disparo do WhatsApp"
                      >
                        Reenviar para a Fila
                      </Button>
                    )}

                    {selectedOfferForDrawer.status === 'archived' && (
                      <Button
                        variant="primary"
                        size="sm"
                        leftIcon={<RotateCcw className="h-3.5 w-3.5" />}
                        onClick={() =>
                          handleTransitionStatus(
                            selectedOfferForDrawer,
                            'pending'
                          )
                        }
                      >
                        Restaurar
                      </Button>
                    )}
                  </div>
                </div>
              ) : undefined
            }
          >
            {selectedOfferForDrawer && (
              <div className="space-y-4">
                {/* 2. Link da Oferta & Ações Compactas de Cópia com Tooltip */}
                <div className="space-y-2 rounded-xl border border-surface-border bg-surface-subtle p-3">
                  <div className="flex items-center justify-between">
                    <span className="text-micro font-semibold uppercase tracking-wider text-content-muted">
                      Link de Redirecionamento
                    </span>
                    <div className="flex items-center gap-1.5">
                      <button
                        type="button"
                        onClick={() =>
                          handleCopyTrackedUrl(
                            selectedOfferForDrawer.group_tracked_url ||
                              `${selectedOfferForDrawer.tracked_url}?src=whatsapp_group`,
                            `${selectedOfferForDrawer.id}-group`
                          )
                        }
                        title="Link rastreado para grupos do WhatsApp (?src=whatsapp_group)"
                        className="flex cursor-pointer items-center gap-1 rounded-md border border-brand-500/30 bg-brand-500/10 px-2 py-1 text-2xs font-semibold text-brand-600 hover:bg-brand-500/20 dark:text-brand-400"
                      >
                        {copiedLink === `${selectedOfferForDrawer.id}-group` ? (
                          <Check className="h-3 w-3 text-brand-500" />
                        ) : (
                          <Copy className="h-3 w-3" />
                        )}
                        <span>Link Grupos</span>
                      </button>

                      <button
                        type="button"
                        onClick={() =>
                          handleCopyTrackedUrl(
                            selectedOfferForDrawer.tracked_url,
                            `${selectedOfferForDrawer.id}-base`
                          )
                        }
                        title="Copiar Link Base Curto (/r/:code)"
                        className="flex cursor-pointer items-center gap-1 rounded-md border border-surface-border bg-surface-panel px-2 py-1 text-2xs font-semibold text-content-muted hover:text-content-main"
                      >
                        {copiedLink === `${selectedOfferForDrawer.id}-base` ? (
                          <Check className="h-3 w-3 text-brand-500" />
                        ) : (
                          <Copy className="h-3 w-3" />
                        )}
                        <span>Link Base</span>
                      </button>
                    </div>
                  </div>

                  <div className="rounded-lg border border-surface-border bg-surface-panel p-2 font-mono text-xs text-content-muted">
                    <span className="break-all">
                      {selectedOfferForDrawer.tracked_url}
                    </span>
                  </div>
                </div>

                {/* Controle de Visibilidade na Vitrine Pública */}
                <div className="flex items-center justify-between rounded-xl border border-surface-border bg-surface-panel p-3.5">
                  <div className="space-y-0.5">
                    <div className="flex items-center gap-1.5">
                      <span className="text-xs font-semibold text-content-main">
                        Vitrine Pública
                      </span>
                      <Badge
                        variant={
                          selectedOfferForDrawer.show_in_showcase !== false
                            ? 'online'
                            : 'offline'
                        }
                        className="text-micro"
                      >
                        {selectedOfferForDrawer.show_in_showcase !== false
                          ? 'Visível'
                          : 'Oculta'}
                      </Badge>
                    </div>
                    <p className="text-2xs text-content-muted">
                      {selectedOfferForDrawer.status === 'sent'
                        ? selectedOfferForDrawer.show_in_showcase !== false
                          ? 'Esta oferta está ativa na vitrine pública do seu canal.'
                          : 'Esta oferta está oculta na vitrine pública.'
                        : 'Será exibida na vitrine pública após ser disparada pela Fila.'}
                    </p>
                  </div>
                  <Button
                    variant={
                      selectedOfferForDrawer.show_in_showcase !== false
                        ? 'secondary'
                        : 'primary'
                    }
                    size="sm"
                    isLoading={isUpdatingDrawer}
                    onClick={handleToggleDrawerShowcase}
                  >
                    {selectedOfferForDrawer.show_in_showcase !== false
                      ? 'Ocultar da Vitrine'
                      : 'Exibir na Vitrine'}
                  </Button>
                </div>

                {/* 4. Preview WhatsApp Colapsável no Drawer */}
                <div className="space-y-2 rounded-xl border border-surface-border bg-surface-panel p-3">
                  <div className="flex items-center justify-between">
                    <span className="text-micro font-semibold uppercase tracking-wider text-content-muted">
                      Preview WhatsApp
                    </span>
                    <button
                      type="button"
                      onClick={() =>
                        setIsDrawerPreviewExpanded(!isDrawerPreviewExpanded)
                      }
                      className="cursor-pointer text-2xs font-semibold text-brand-600 hover:underline dark:text-brand-400"
                    >
                      {isDrawerPreviewExpanded
                        ? 'Colapsar'
                        : 'Visualizar Mensagem'}
                    </button>
                  </div>

                  {isDrawerPreviewExpanded && (
                    <div className="pt-2">
                      <WhatsAppBubblePreview
                        imageUrl={selectedOfferForDrawer.image_url}
                        title={selectedOfferForDrawer.title}
                        text={(() => {
                          const rawText =
                            selectedOfferForDrawer.selected_copy ||
                            selectedOfferForDrawer.title;
                          const shortUrl =
                            selectedOfferForDrawer.group_tracked_url ||
                            selectedOfferForDrawer.tracked_url;
                          if (!shortUrl) return rawText;

                          let processed = rawText;
                          if (
                            selectedOfferForDrawer.link_type !== 'marketplace'
                          ) {
                            if (selectedOfferForDrawer.affiliate_url) {
                              processed = processed
                                .split(selectedOfferForDrawer.affiliate_url)
                                .join(shortUrl);
                            }
                            if (selectedOfferForDrawer.original_url) {
                              processed = processed
                                .split(selectedOfferForDrawer.original_url)
                                .join(shortUrl);
                            }
                          }
                          return processed;
                        })()}
                        originalPrice={selectedOfferForDrawer.original_price}
                        promotionalPrice={
                          selectedOfferForDrawer.promotional_price
                        }
                        couponCode={
                          selectedOfferForDrawer.coupon_code || undefined
                        }
                        isCouponOnly={selectedOfferForDrawer.is_coupon_only}
                        marketplace={selectedOfferForDrawer.marketplace}
                      />
                    </div>
                  )}
                </div>

                {/* 5. Telemetria de Cliques (Exclusiva para Ofertas Enviadas / Pós-envio) */}
                {(selectedOfferForDrawer.status === 'sent' ||
                  selectedOfferForDrawer.sent_at) && (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-micro font-semibold uppercase tracking-wider text-content-muted">
                        Telemetria de Cliques
                      </span>
                      <span className="font-mono text-xs font-bold text-content-main">
                        {selectedOfferForDrawer.clicks_count} cliques
                      </span>
                    </div>

                    {Object.keys(selectedOfferForDrawer.clicks_by_source || {})
                      .length === 0 ? (
                      <p className="rounded-lg border border-surface-border bg-surface-subtle p-2.5 text-2xs text-content-muted">
                        Nenhum clique com parâmetro (?src=...) registrado até o
                        momento.
                      </p>
                    ) : (
                      <div className="space-y-1.5 rounded-xl border border-surface-border bg-surface-subtle p-3">
                        {Object.entries(
                          selectedOfferForDrawer.clicks_by_source
                        ).map(([src, count]) => {
                          const percent = selectedOfferForDrawer.clicks_count
                            ? Math.round(
                                (count / selectedOfferForDrawer.clicks_count) *
                                  100
                              )
                            : 0;
                          return (
                            <div
                              key={src}
                              className="flex items-center justify-between text-xs"
                            >
                              <div className="flex items-center gap-1.5">
                                <span className="font-mono text-xs font-medium text-content-main">
                                  {src}
                                </span>
                                <Badge variant="neutral" className="text-2xs">
                                  {percent}%
                                </Badge>
                              </div>
                              <span className="font-semibold text-content-main">
                                {count} cliques
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}

                {/* 6. Histórico de Ações Colapsável e Compacto */}
                <div className="space-y-2 rounded-xl border border-surface-border bg-surface-subtle p-3">
                  <div className="flex items-center justify-between">
                    <button
                      type="button"
                      onClick={() => setIsHistoryExpanded(!isHistoryExpanded)}
                      className="flex cursor-pointer items-center gap-1.5 text-xs font-semibold text-content-main hover:text-brand-600 dark:hover:text-brand-400"
                    >
                      <History className="h-3.5 w-3.5 text-content-muted" />
                      <span>Histórico de Ações</span>
                      <Badge
                        variant="neutral"
                        className="text-micro font-semibold"
                      >
                        {selectedOfferForDrawer.status_history.length}
                      </Badge>
                      {isHistoryExpanded ? (
                        <ChevronDown className="h-3.5 w-3.5 text-content-muted" />
                      ) : (
                        <ChevronRight className="h-3.5 w-3.5 text-content-muted" />
                      )}
                    </button>

                    {isHistoryExpanded &&
                      selectedOfferForDrawer.status_history.length > 5 && (
                        <div className="flex items-center gap-1 font-mono text-2xs text-content-muted">
                          <button
                            type="button"
                            disabled={auditPage <= 1}
                            onClick={() =>
                              setAuditPage((p) => Math.max(1, p - 1))
                            }
                            className="cursor-pointer rounded px-1.5 py-0.5 hover:bg-surface-panel disabled:opacity-30"
                          >
                            Anterior
                          </button>
                          <span>
                            {auditPage} /{' '}
                            {Math.ceil(
                              selectedOfferForDrawer.status_history.length / 5
                            )}
                          </span>
                          <button
                            type="button"
                            disabled={
                              auditPage >=
                              Math.ceil(
                                selectedOfferForDrawer.status_history.length / 5
                              )
                            }
                            onClick={() => setAuditPage((p) => p + 1)}
                            className="cursor-pointer rounded px-1.5 py-0.5 hover:bg-surface-panel disabled:opacity-30"
                          >
                            Próxima
                          </button>
                        </div>
                      )}
                  </div>

                  {isHistoryExpanded && (
                    <div className="pt-1">
                      {selectedOfferForDrawer.status_history.length === 0 ? (
                        <p className="text-2xs text-content-muted">
                          Nenhuma transição registrada no histórico.
                        </p>
                      ) : (
                        <div className="divide-y divide-surface-border rounded-lg border border-surface-border bg-surface-panel">
                          {[...selectedOfferForDrawer.status_history]
                            .reverse()
                            .slice((auditPage - 1) * 5, auditPage * 5)
                            .map((hist, idx) => {
                              const isNoteExpanded =
                                expandedNoteIndices.has(idx);
                              return (
                                <div
                                  key={idx}
                                  className="flex items-center justify-between gap-3 px-3 py-1.5 text-micro"
                                >
                                  {/* Lado Esquerdo/Centro: Ação + Descrição compacta e expansível */}
                                  <div className="flex min-w-0 flex-1 items-center gap-2">
                                    <Badge
                                      variant={
                                        hist.action === 'created'
                                          ? 'info'
                                          : hist.action === 'queued'
                                            ? 'warning'
                                            : hist.action === 'paused'
                                              ? 'neutral'
                                              : hist.action === 'hotfix_url'
                                                ? 'warning'
                                                : hist.action === 'archived'
                                                  ? 'offline'
                                                  : 'online'
                                      }
                                      className="flex-shrink-0 px-1.5 py-0.5 text-2xs font-semibold"
                                    >
                                      {hist.action === 'created'
                                        ? 'Criada'
                                        : hist.action === 'queued'
                                          ? 'Na Fila'
                                          : hist.action === 'paused'
                                            ? 'Pausada'
                                            : hist.action === 'hotfix_url'
                                              ? 'Hotfix'
                                              : hist.action === 'archived'
                                                ? 'Arquivada'
                                                : hist.action === 'restored'
                                                  ? 'Restaurada'
                                                  : hist.action}
                                    </Badge>
                                    {hist.note ? (
                                      <button
                                        type="button"
                                        onClick={() => {
                                          const next = new Set(
                                            expandedNoteIndices
                                          );
                                          if (next.has(idx)) {
                                            next.delete(idx);
                                          } else {
                                            next.add(idx);
                                          }
                                          setExpandedNoteIndices(next);
                                        }}
                                        title={
                                          isNoteExpanded
                                            ? 'Clique para recolher'
                                            : 'Clique para ver a descrição completa'
                                        }
                                        className={`cursor-pointer text-left text-content-muted transition-colors hover:text-content-main ${
                                          isNoteExpanded
                                            ? 'break-words text-content-main'
                                            : 'truncate'
                                        }`}
                                      >
                                        {hist.note}
                                      </button>
                                    ) : (
                                      <span className="text-content-muted/60 text-2xs italic">
                                        Sem observações
                                      </span>
                                    )}
                                  </div>

                                  {/* Lado Direito: Usuário acima e Data formatada abaixo */}
                                  <div className="flex-shrink-0 text-right">
                                    <div className="max-w-32 truncate text-micro font-medium text-content-main">
                                      {hist.user_email || 'Sistema'}
                                    </div>
                                    <div className="font-mono text-2xs text-content-muted">
                                      {new Date(
                                        hist.changed_at
                                      ).toLocaleTimeString('pt-BR', {
                                        hour: '2-digit',
                                        minute: '2-digit',
                                        day: '2-digit',
                                        month: '2-digit',
                                      })}
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}
          </Drawer>

          {/* 6. Modal de Confirmação de Exclusão Individual */}
          <ConfirmationModal
            isOpen={Boolean(deletingOffer)}
            onClose={() => setDeletingOffer(null)}
            onConfirm={handleDeleteOffer}
            title="Excluir Oferta"
            description={`Deseja realmente remover a promoção "${deletingOffer?.title}"? O link curto correspondente (/r/${deletingOffer?.short_code}) deixará de funcionar definitivamente.`}
            confirmText="Excluir Definitivamente"
            cancelText="Cancelar"
            variant="danger"
            isLoading={isDeleting}
          />

          {/* 7. Modal de Confirmação de Exclusão em Lote */}
          <ConfirmationModal
            isOpen={bulkDeleteModalOpen}
            onClose={() => setBulkDeleteModalOpen(false)}
            onConfirm={handleConfirmBulkDelete}
            title="Excluir Ofertas Selecionadas"
            description={`Tem certeza que deseja excluir definitivamente as ${selectedOfferIds.size} ofertas selecionadas? Os links de redirecionamento correspondentes deixarão de funcionar.`}
            confirmText="Excluir Selecionadas"
            cancelText="Cancelar"
            variant="danger"
            isLoading={isBulkActing}
          />
        </>
      )}

      {/* 8. Drawer de Aplicação Rápida de Cupom na Oferta */}
      {couponTargetOffer && (
        <ApplyCouponDrawer
          isOpen={Boolean(couponTargetOffer)}
          onClose={() => setCouponTargetOffer(null)}
          targetType="offer"
          targetId={couponTargetOffer.id}
          targetTitle={couponTargetOffer.title}
          currentPrice={couponTargetOffer.promotional_price}
          originalPrice={couponTargetOffer.original_price}
          marketplace={couponTargetOffer.marketplace}
          onApplied={async () => {
            await loadData();
          }}
          applyFn={applyCouponToOffer}
        />
      )}

      {/* 9. Drawer de Seleção de Fila para Envio em Lote (Ticket 06) */}
      <Drawer
        isOpen={isQueueSelectorOpen}
        onClose={() => setIsQueueSelectorOpen(false)}
        title="Enviar Ofertas Selecionadas para a Fila"
        description={`Selecione a fila de destino para o disparo de ${selectedOfferIds.size} oferta(s).`}
        footer={
          <div className="flex w-full items-center justify-end gap-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setIsQueueSelectorOpen(false)}
            >
              Cancelar
            </Button>
            <Button
              variant="primary"
              size="sm"
              leftIcon={<Send className="h-3.5 w-3.5" />}
              isLoading={isBulkActing}
              disabled={
                isBulkActing || !selectedQueueId || selectedOfferIds.size === 0
              }
              onClick={handleConfirmBulkQueue}
            >
              Confirmar Envio ({selectedOfferIds.size})
            </Button>
          </div>
        }
      >
        <div className="space-y-4 p-4 text-xs">
          <div>
            <label className="mb-1 block font-semibold text-content-main">
              Fila de Disparo de Destino
            </label>
            {isLoadingQueues ? (
              <div className="flex items-center gap-2 py-4 text-content-muted">
                <RefreshCw className="h-4 w-4 animate-spin" />
                <span>Carregando filas...</span>
              </div>
            ) : dispatchQueues.length === 0 ? (
              <div className="rounded-lg border border-surface-border bg-surface-subtle p-3 text-content-muted">
                Nenhuma fila secundária encontrada. A fila principal padrão será
                utilizada.
              </div>
            ) : (
              <div className="space-y-2">
                {dispatchQueues.map((queue) => {
                  const isSelected = selectedQueueId === queue.id;
                  return (
                    <div
                      key={queue.id}
                      onClick={() => setSelectedQueueId(queue.id)}
                      className={`flex cursor-pointer items-center justify-between rounded-lg border p-3 transition-colors ${
                        isSelected
                          ? 'border-brand-500 bg-brand-500/10'
                          : 'hover:border-surface-border/80 border-surface-border bg-surface-panel'
                      }`}
                    >
                      <div className="space-y-0.5">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-content-main">
                            {queue.name}
                          </span>
                          {(queue.slug === 'principal' ||
                            queue.priority === 1) && (
                            <Badge
                              variant="online"
                              className="text-micro font-bold"
                            >
                              Principal
                            </Badge>
                          )}
                          {queue.is_paused && (
                            <Badge variant="offline" className="text-micro">
                              Pausada
                            </Badge>
                          )}
                        </div>
                        {queue.strategy_type && (
                          <p className="text-2xs text-content-muted">
                            Estratégia: {queue.strategy_type}
                          </p>
                        )}
                      </div>
                      <input
                        type="radio"
                        name="target_queue"
                        checked={isSelected}
                        onChange={() => setSelectedQueueId(queue.id)}
                        className="h-4 w-4 text-brand-600 focus:ring-brand-500"
                      />
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="rounded-lg border border-surface-border bg-surface-subtle p-3">
            <div className="flex items-center gap-2 font-semibold text-content-main">
              <Layers className="h-4 w-4 text-brand-500" />
              <span>Resumo do lote</span>
            </div>
            <div className="mt-2 space-y-1 text-2xs text-content-muted">
              <div>
                Total de ofertas selecionadas:{' '}
                <strong className="text-content-main">
                  {selectedOfferIds.size}
                </strong>
              </div>
              <div>
                Status:{' '}
                {Array.from(
                  new Set(selectedOffersList.map((o) => o.status))
                ).join(', ') || 'Nenhum'}
              </div>
            </div>
          </div>
        </div>
      </Drawer>
    </div>
  );
};
